/**
 * AdminCMS — Unified CMS Admin for all content types
 * Replaces 7 separate admin pages with a single unified interface
 */

import { h } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Spinner, Button } from '../components/ui';
import { T } from '../styles/tokens';

// ── Types ──

interface ContentType {
  id: string; slug: string; label: string; label_plural: string; description: string;
  icon: string; color: string; subdomain: string;
  has_categories: number; has_tags: number; has_featured: number;
  has_cover_image: number; has_author: number; custom_fields_schema: string;
  is_active: number; sort_order: number;
}

interface EntryTranslation {
  locale: string; title: string; excerpt: string; body?: string;
  features_json?: string; custom_fields?: string;
  meta_title?: string; meta_description?: string;
  og_title?: string; og_description?: string; og_image?: string;
  body_length?: number; word_count?: number;
  image_count?: number; video_count?: number; link_count?: number; heading_count?: number;
}

interface Entry {
  id: string; content_type_id: string; slug: string; category_id: string;
  author_name: string; author_avatar: string; cover_image: string;
  is_featured: number; is_popular: number; sort_order: number;
  tags: string; metadata: string; status: string;
  view_count: number; like_count: number;
  published_at: string; scheduled_at: string;
  created_at: string; updated_at: string;
  translations: EntryTranslation[];
  category_slug?: string; category_color?: string;
}

interface Category {
  id: string; content_type_id: string; slug: string; parent_id: string;
  icon: string; color: string; sort_order: number; is_active: number;
  status?: string;
  created_at: string; entry_count?: number; translation_count?: number;
  translations?: { locale: string; name: string; description: string; slug?: string }[];
}

interface Menu {
  id: string; content_type_id: string; slug: string; location: string;
  items_json: any[]; is_active: number; created_at: string;
}

interface AuditLog {
  id: string; user_id: string; user_name: string; action: string;
  entity_type: string; entity_id: string; details: string; created_at: string;
}

interface Setting {
  id: string; content_type_id: string; setting_key: string;
  setting_value: string; created_at: string;
}

interface Analytics {
  top_entries: { id: string; slug: string; title: string; view_count: number; like_count: number }[];
  recent_events: { event_type: string; count: number }[];
  totals: { views: number; likes: number; total_entries: number };
}

// ── Styles (identical to AdminBlog) ──

const S = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #0a0e1a 0%, #0d1117 100%)', color: T.text, fontFamily: "'Inter', -apple-system, sans-serif", padding: '16px 12px 80px' } as any,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } as any,
  title: { fontSize: 20, fontWeight: 600, margin: 0, color: T.text } as any,
  tabs: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.borderHover}`, marginBottom: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' } as any,
  tab: (active: boolean) => ({
    padding: '10px 16px', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
    color: active ? T.text : T.textSecondary, borderBottom: active ? `2px solid ${T.primary}` : '2px solid transparent',
    background: 'transparent', border: 'none', transition: 'all 0.15s', whiteSpace: 'nowrap' as const, flexShrink: 0,
  }),
  card: { background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 6, padding: 16, marginBottom: 12 } as any,
  input: { width: '100%', padding: '8px 10px', fontSize: 13, background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 4, color: T.text, outline: 'none', boxSizing: 'border-box' as const } as any,
  textarea: { width: '100%', padding: '8px 10px', fontSize: 13, background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 4, color: T.text, outline: 'none', minHeight: 600, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", resize: 'vertical' as const, boxSizing: 'border-box' as const, lineHeight: 1.6 } as any,
  btn: (variant: 'primary' | 'secondary' | 'danger' | 'warning' = 'primary') => ({
    padding: '5px 10px', fontSize: 11, fontWeight: 500, borderRadius: 3, border: 'none', cursor: 'pointer',
    background: variant === 'primary' ? T.primary : variant === 'danger' ? T.dangerHover : variant === 'warning' ? T.warningHover : T.borderHover,
    color: T.text, transition: 'opacity 0.15s', whiteSpace: 'nowrap' as const,
  }),
  label: { fontSize: 11, fontWeight: 500, color: T.textSecondary, marginBottom: 4, display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.5px' } as any,
  tableWrap: { overflowX: 'auto' as const, WebkitOverflowScrolling: 'touch' as const } as any,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12, tableLayout: 'auto' as const, minWidth: 600 },
  th: { textAlign: 'left' as const, padding: '6px 8px', color: T.textSecondary, fontSize: 10, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: `1px solid ${T.borderHover}`, background: T.bgModal, whiteSpace: 'nowrap' as const },
  td: { padding: '6px 8px', borderBottom: `1px solid ${T.bgHover}80` },
  badge: (color: string) => ({ padding: '2px 6px', borderRadius: 3, fontSize: 11, fontWeight: 500, background: `${color}18`, color, border: `1px solid ${color}30` }),
  modalOverlay: { position: 'fixed' as const, inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 6, padding: 20, width: '90%', maxWidth: 800, maxHeight: '90vh', overflowY: 'auto' as const },
  row: { display: 'flex', gap: 12, marginBottom: 12 } as any,
  statsCard: { flex: 1, background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 6, padding: '16px 12px', textAlign: 'center' as const },
  statsVal: { fontSize: 24, fontWeight: 600, color: T.text, margin: 0 } as any,
  statsLabel: { fontSize: 11, color: T.textSecondary, margin: '4px 0 0', textTransform: 'uppercase' as const } as any,
  select: { width: '100%', padding: '8px 10px', fontSize: 13, background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 4, color: T.text, outline: 'none', boxSizing: 'border-box' as const } as any,
  toggle: (on: boolean) => ({
    width: 36, height: 20, borderRadius: 10, background: on ? T.success : T.textDisabled,
    position: 'relative' as const, cursor: 'pointer', transition: 'background 0.2s', border: 'none', padding: 0,
  }),
  toggleDot: (on: boolean) => ({
    width: 14, height: 14, borderRadius: '50%', background: '#fff',
    position: 'absolute' as const, top: 3, left: on ? 19 : 3, transition: 'left 0.2s',
  }),
  pill: (active: boolean, color: string) => ({
    padding: '5px 12px', fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer',
    color: active ? T.text : T.textSecondary,
    background: active ? `${color}12` : 'transparent',
    border: active ? `1px solid ${color}60` : `1px solid ${T.borderHover}`,
    borderRadius: 6, transition: 'all 0.2s ease', display: 'inline-flex', alignItems: 'center', gap: 6,
    boxShadow: active ? `0 0 8px ${color}20` : 'none',
  }),
  seoBadge: (score: number) => ({
    padding: '2px 6px', borderRadius: 3, fontSize: 11, fontWeight: 600,
    background: `${score > 70 ? T.success : score >= 40 ? T.warning : T.danger}18`,
    color: score > 70 ? T.success : score >= 40 ? T.warning : T.danger,
    border: `1px solid ${score > 70 ? T.success : score >= 40 ? T.warning : T.danger}30`,
  }),
};

function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// ── Inline Markdown Parser ──

function parseMarkdown(md: string): string {
  if (!md) return '';
  let html = md;
  // Code blocks (```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
    const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<pre style="background:${T.bgSecondary};padding:12px;border-radius:4px;overflow-x:auto;border:1px solid ${T.bgHover}"><code class="lang-${lang}">${escaped}</code></pre>`;
  });
  // Inline code
  html = html.replace(/`([^`]+)`/g, `<code style="background:${T.bgSecondary};padding:2px 6px;border-radius:3px;font-size:0.9em">$1</code>`);
  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;border-radius:4px;margin:8px 0" />');
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" style="color:${T.primary};text-decoration:none" target="_blank">$1</a>`);
  // Headings
  html = html.replace(/^######\s+(.+)$/gm, `<h6 style="font-size:13px;margin:12px 0 4px;color:${T.text}">$1</h6>`);
  html = html.replace(/^#####\s+(.+)$/gm, '<h5 style="font-size:14px;margin:12px 0 4px;color:${T.text}">$1</h5>');
  html = html.replace(/^####\s+(.+)$/gm, '<h4 style="font-size:15px;margin:14px 0 6px;color:${T.text}">$1</h4>');
  html = html.replace(/^###\s+(.+)$/gm, '<h3 style="font-size:16px;margin:16px 0 6px;color:${T.text}">$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h2 style="font-size:18px;margin:18px 0 8px;color:${T.text}">$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1 style="font-size:22px;margin:20px 0 8px;color:${T.text}">$1</h1>');
  // Horizontal rule
  html = html.replace(/^---$/gm, `<hr style="border:none;border-top:1px solid ${T.bgHover};margin:16px 0" />`);
  // Blockquote
  html = html.replace(/^>\s+(.+)$/gm, `<blockquote style="border-left:3px solid ${T.primary};padding:4px 12px;margin:8px 0;color:${T.textSecondary}">$1</blockquote>`);
  // Bold + Italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Unordered list
  html = html.replace(/^[-*]\s+(.+)$/gm, '<li style="margin:2px 0">$1</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>\n?)+/g, (match) => `<ul style="padding-left:20px;margin:8px 0">${match}</ul>`);
  // Paragraphs
  html = html.replace(/\n\n+/g, '\n\n');
  const parts = html.split('\n\n');
  html = parts.map(p => {
    const trimmed = p.trim();
    if (!trimmed) return '';
    if (/^<(h[1-6]|pre|ul|ol|li|blockquote|hr|img|div)/.test(trimmed)) return trimmed;
    return `<p style="margin:8px 0;line-height:1.6">${trimmed.replace(/\n/g, '<br />')}</p>`;
  }).join('\n');
  return html;
}

function parseTags(t: string): string[] {
  try { return JSON.parse(t || '[]'); } catch { return t ? t.split(',').map(s => s.trim()) : []; }
}

function parseCustomFields(schema: string): { key: string; label: string; type: string; options?: string[] }[] {
  try { return JSON.parse(schema || '[]'); } catch { return []; }
}

function formatCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n || 0);
}

function calcSeoScore(entry: Entry, translation: EntryTranslation | undefined): number {
  let score = 0;
  const t = translation;
  if (!t) return 0;
  // meta_title 30-60 chars
  const mtLen = (t.meta_title || '').length;
  if (mtLen >= 30 && mtLen <= 60) score += 15;
  else if (mtLen > 0) score += 5;
  // meta_description 120-160 chars
  const mdLen = (t.meta_description || '').length;
  if (mdLen >= 120 && mdLen <= 160) score += 15;
  else if (mdLen > 0) score += 5;
  // title 30-70 chars
  const tLen = (t.title || '').length;
  if (tLen >= 30 && tLen <= 70) score += 10;
  else if (tLen > 0) score += 3;
  // excerpt >50 chars
  if ((t.excerpt || '').length > 50) score += 10;
  // word count
  const wc = t.word_count || 0;
  if (wc > 1500) score += 20;
  else if (wc > 800) score += 15;
  else if (wc > 300) score += 10;
  // cover_image
  if (entry.cover_image) score += 10;
  // category
  if (entry.category_id) score += 5;
  // tags >=3
  const tags = parseTags(entry.tags);
  if (tags.length >= 3) score += 5;
  // slug clean
  if (entry.slug && !/[^a-z0-9-]/.test(entry.slug)) score += 5;
  // media factors
  if ((t.image_count || 0) >= 1) score += 5;
  if ((t.video_count || 0) >= 1) score += 5;
  if ((t.link_count || 0) >= 2) score += 5;
  if ((t.heading_count || 0) >= 3) score += 5;
  return Math.min(score, 100);
}

function seoColor(score: number): string {
  if (score > 70) return T.success;
  if (score >= 40) return T.warning;
  return T.danger;
}

// ── Main Component ──

export function AdminCMSPage() {
  // Content types
  const [types, setTypes] = useState<ContentType[]>([]);
  const [selectedType, setSelectedType] = useState<ContentType | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [subTab, setSubTab] = useState<'entries' | 'categories' | 'analytics' | 'menus' | 'settings' | 'audit' | 'users' | 'media' | 'global-config' | 'sitemap' | 'costs' | 'languages' | 'monitoring' | 'scheduling' | 'llm-test'>('entries');
  const [sitemapData, setSitemapData] = useState<{ entries: any[]; categories: any[]; types: any[] }>({ entries: [], categories: [], types: [] });
  const [sitemapFilter, setSitemapFilter] = useState('');
  const [costsList, setCostsList] = useState<any[]>([]);
  const [costsSummary, setCostsSummary] = useState<any>(null);
  const [costsPeriod, setCostsPeriod] = useState('30');
  const [entryCosts, setEntryCosts] = useState<{ costs: any[]; total_usd: number } | null>(null);
  const [showEntryCostDetails, setShowEntryCostDetails] = useState(false);

  // Languages tab
  const [allLocales, setAllLocales] = useState<{ code: string; name: string; native_name: string; is_active: number }[]>([]);
  const [localeSearch, setLocaleSearch] = useState('');
  const [localesLoading, setLocalesLoading] = useState(false);

  // Monitoring tab
  const [monitoringStats, setMonitoringStats] = useState<any>(null);
  const [monitoringRecent, setMonitoringRecent] = useState<any[]>([]);
  const [monitoringPeriod, setMonitoringPeriod] = useState('30d');
  const [monitoringLoading, setMonitoringLoading] = useState(false);

  // Scheduling tab
  const [schedRules, setSchedRules] = useState<any[]>([]);
  const [schedQueue, setSchedQueue] = useState<any[]>([]);
  const [schedCalendar, setSchedCalendar] = useState<Record<string, any[]>>({});
  const [schedHistory, setSchedHistory] = useState<any[]>([]);
  const [schedHistoryTotal, setSchedHistoryTotal] = useState(0);
  const [schedHistoryPage, setSchedHistoryPage] = useState(1);
  const [schedHistoryFilter, setSchedHistoryFilter] = useState('');
  const [schedCalMonth, setSchedCalMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedShowRuleModal, setSchedShowRuleModal] = useState(false);
  const [schedEditRule, setSchedEditRule] = useState<any>(null);
  const [schedShowQueueModal, setSchedShowQueueModal] = useState(false);
  const [schedShowCalendar, setSchedShowCalendar] = useState(true);
  const [schedShowHistory, setSchedShowHistory] = useState(false);
  const [schedProcessing, setSchedProcessing] = useState(false);

  const [allEntriesRaw, setAllEntriesRaw] = useState<Entry[]>([]);
  const [allEntriesTotal, setAllEntriesTotal] = useState(0);

  // Entries
  const [entries, setEntries] = useState<Entry[]>([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [entriesPage, setEntriesPage] = useState(0);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grouped' | 'ungrouped'>('grouped');
  const [viewLayout, setViewLayout] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'views' | 'seo'>('date');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  // Categories
  const [categories, setCategories] = useState<Category[]>([]);

  // Analytics
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // Menus
  const [menus, setMenus] = useState<Menu[]>([]);

  // Settings
  const [settings, setSettings] = useState<Setting[]>([]);
  const [settingsEdited, setSettingsEdited] = useState<Record<string, string>>({});
  const [newSettingKey, setNewSettingKey] = useState('');
  const [newSettingVal, setNewSettingVal] = useState('');

  // Audit
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditDays, setAuditDays] = useState(30);

  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editEntry, setEditEntry] = useState<Entry | null>(null);
  const [editCat, setEditCat] = useState<Category | null>(null);
  const [editMenu, setEditMenu] = useState<Menu | null>(null);

  // Entry form
  const [fSlug, setFSlug] = useState('');
  const [fCatId, setFCatId] = useState('');
  const [fCover, setFCover] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [fAuthor, setFAuthor] = useState('');
  const [fTags, setFTags] = useState('');
  const [fFeatured, setFFeatured] = useState(false);
  const [fScheduled, setFScheduled] = useState('');
  const [fCodeBlocks, setFCodeBlocks] = useState<{ id: number; language: string; label: string; code: string }[]>([]);
  const [fLocale, setFLocale] = useState('pt-BR');
  // Translation fields per locale
  const [fTranslations, setFTranslations] = useState<Record<string, { title: string; excerpt: string; body: string; meta_title: string; meta_description: string; custom_fields: Record<string, any> }>>({
    'pt-BR': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
    'en': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
  });

  // Category form
  const [cfSlug, setCfSlug] = useState('');
  const [cfColor, setCfColor] = useState(T.primary);
  const [cfIcon, setCfIcon] = useState('');
  const [cfSortOrder, setCfSortOrder] = useState(0);
  const [cfLocale, setCfLocale] = useState('pt-BR');
  const [catTranslateModel, setCatTranslateModel] = useState('anthropic/claude-sonnet-4-5');
  const [catTranslating, setCatTranslating] = useState(false);
  const [catTranslateResult, setCatTranslateResult] = useState<{ translated: number; failed: number; total: number; failed_locales: string[] } | null>(null);
  const [cfTranslations, setCfTranslations] = useState<Record<string, { name: string; description: string }>>({
    'pt-BR': { name: '', description: '' },
    'en': { name: '', description: '' },
  });

  // Translate
  const [translateModel, setTranslateModel] = useState('anthropic/claude-sonnet-4-5');
  const [translating, setTranslating] = useState(false);
  const [restyling, setRestyling] = useState(false);
  const [generatingInlineImages, setGeneratingInlineImages] = useState(false);
  const [generatingIdea, setGeneratingIdea] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [translateResult, setTranslateResult] = useState<{ translated: number; failed: number; total: number; failed_locales: string[] } | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>(['anthropic/claude-sonnet-4-5', 'anthropic/claude-haiku-4-5', 'openai/gpt-4o', 'openai/gpt-4o-mini']);
  const [providers, setProviders] = useState<{provider: string, label: string}[]>([]);

  // Menu form
  const [mfSlug, setMfSlug] = useState('');
  const [mfLocation, setMfLocation] = useState('');
  const [mfItemsJson, setMfItemsJson] = useState('[]');

  // Markdown editor mode
  const [editorMode, setEditorMode] = useState<'write' | 'preview' | 'split'>('write');

  // CMS Users (Feature 2)
  const [cmsUsers, setCmsUsers] = useState<any[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUserCustomerId, setNewUserCustomerId] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('editor');

  // Media (Feature 3)
  const [mediaItems, setMediaItems] = useState<any[]>([]);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaFolder, setMediaFolder] = useState('');
  const [showMediaUrlModal, setShowMediaUrlModal] = useState(false);
  const [newMediaUrl, setNewMediaUrl] = useState('');
  const [newMediaAlt, setNewMediaAlt] = useState('');
  const [newMediaFolder, setNewMediaFolder] = useState('');

  // Global Config (Feature 4)
  const [globalSettings, setGlobalSettings] = useState<Record<string, string>>({});
  const [globalSettingsLoading, setGlobalSettingsLoading] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);

  // Per-operation models cache (provider -> models[])
  const [opModelsCache, setOpModelsCache] = useState<Record<string, string[]>>({});
  const [opModelsLoading, setOpModelsLoading] = useState<Record<string, boolean>>({});

  // LLM Test tab state
  const [llmTestUrl, setLlmTestUrl] = useState('/api/v1/llm/v1');
  const [llmTestToken, setLlmTestToken] = useState('');
  const [llmTestModels, setLlmTestModels] = useState<string[]>([]);
  const [llmTestModel, setLlmTestModel] = useState('');
  const [llmTestMsg, setLlmTestMsg] = useState('Diga oi em 3 palavras');
  const [llmTestLoading, setLlmTestLoading] = useState(false);
  const [llmTestFetchingModels, setLlmTestFetchingModels] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState<any>(null);

  // Entry counts per type
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});

  const fetchTypeCounts = useCallback(async (contentTypes: ContentType[]) => {
    const counts: Record<string, number> = {};
    let grandTotal = 0;
    try {
      // Fetch all entries with no type filter to get total
      const r = await api.get<any>('/admin/cms/entries?limit=1&offset=0');
      grandTotal = r.data?.total || r.total || 0;
    } catch { /* ignore */ }
    // Fetch per-type counts
    for (const ct of contentTypes) {
      try {
        const r = await api.get<any>(`/admin/cms/entries?contentType=${ct.id}&limit=1&offset=0`);
        counts[ct.id] = r.data?.total || r.total || 0;
      } catch { counts[ct.id] = 0; }
    }
    counts['__all__'] = grandTotal;
    setTypeCounts(counts);
  }, []);

  // ── Fetch content types ──
  const fetchTypes = useCallback(async () => {
    try {
      const r = await api.get<any>('/admin/cms/types');
      const t = (r.data?.types || r.types || []) as ContentType[];
      setTypes(t);
      if (t.length > 0 && !selectedType && !showAll) setSelectedType(t[0]);
      fetchTypeCounts(t);
    } catch { /* ignore */ }
  }, []);

  // ── Fetch entries ──
  const fetchEntries = useCallback(async () => {
    if (!selectedType && !showAll) return;
    setEntriesLoading(true);
    try {
      const params = new URLSearchParams();
      if (!showAll && selectedType) params.set('contentType', selectedType.id);
      params.set('limit', '20');
      params.set('offset', String(entriesPage * 20));
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      if (filterSearch) params.set('search', filterSearch);
      const r = await api.get<any>(`/admin/cms/entries?${params}`);
      const ents = r.data?.entries || r.entries || [];
      const tot = r.data?.total || r.total || 0;
      if (showAll) {
        setAllEntriesRaw(ents);
        setAllEntriesTotal(tot);
      } else {
        setEntries(ents);
        setEntriesTotal(tot);
      }
    } catch { /* ignore */ }
    setEntriesLoading(false);
  }, [selectedType, showAll, entriesPage, filterStatus, filterCategory, filterSearch]);

  // ── Fetch categories ──
  const fetchCategories = useCallback(async () => {
    if (!selectedType) return;
    try {
      const r = await api.get<any>(`/admin/cms/categories?contentType=${selectedType.id}`);
      setCategories(r.data?.categories || r.categories || []);
    } catch { /* ignore */ }
  }, [selectedType]);

  // ── Fetch analytics ──
  const fetchAnalytics = useCallback(async () => {
    if (!selectedType) return;
    try {
      const r = await api.get<any>(`/admin/cms/analytics?contentType=${selectedType.id}&days=${analyticsDays}`);
      setAnalytics(r.data || r);
    } catch { /* ignore */ }
  }, [selectedType, analyticsDays]);

  // ── Fetch menus ──
  const fetchMenus = useCallback(async () => {
    if (!selectedType) return;
    try {
      const r = await api.get<any>(`/admin/cms/menus?contentType=${selectedType.id}`);
      setMenus(r.data?.menus || r.menus || []);
    } catch { /* ignore */ }
  }, [selectedType]);

  // ── Fetch settings ──
  const fetchSettings = useCallback(async () => {
    if (!selectedType) return;
    try {
      const r = await api.get<any>(`/admin/cms/settings?contentType=${selectedType.id}`);
      setSettings(r.data?.settings || r.settings || []);
      setSettingsEdited({});
    } catch { /* ignore */ }
  }, [selectedType]);

  // ── Fetch audit ──
  const fetchAudit = useCallback(async () => {
    try {
      const r = await api.get<any>(`/admin/cms/audit?days=${auditDays}`);
      setAuditLogs(r.data?.logs || r.logs || []);
    } catch { /* ignore */ }
  }, [auditDays]);

  // ── Fetch CMS Users ──
  const fetchCmsUsers = useCallback(async () => {
    try {
      const r = await api.get<any>('/admin/cms/users');
      setCmsUsers(r.data?.users || r.users || []);
    } catch { /* ignore */ }
  }, []);

  // ── Fetch Media ──
  const fetchMedia = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (mediaFolder) params.set('folder', mediaFolder);
      if (mediaSearch) params.set('search', mediaSearch);
      const r = await api.get<any>(`/admin/cms/media?${params}`);
      setMediaItems(r.data?.media || r.media || []);
    } catch { /* ignore */ }
  }, [mediaFolder, mediaSearch]);

  // ── Fetch Global Settings ──
  const fetchGlobalSettings = useCallback(async () => {
    setGlobalSettingsLoading(true);
    try {
      const r = await api.get<any>('/admin/cms/global-settings');
      const items = r.data?.settings || r.settings || [];
      const map: Record<string, string> = {};
      for (const s of items) map[s.setting_key] = s.setting_value || '';
      setGlobalSettings(map);
    } catch { /* ignore */ }
    try {
      const r2 = await api.get<any>('/admin/cms/providers');
      const provs = r2.data?.providers || r2.providers || [];
      if (Array.isArray(provs) && provs.length) {
        // Deduplicate by provider name
        const seen = new Set<string>();
        const unique = provs.filter((p: any) => {
          if (seen.has(p.provider)) return false;
          seen.add(p.provider);
          return true;
        });
        setProviders(unique);
      }
    } catch { /* ignore */ }
    // Auto-fetch models from Elon Router so provider/model dropdowns are populated
    try {
      const r3 = await api.get<any>('/admin/cms/models');
      const models = r3.data?.models || r3.models || r3 || [];
      if (Array.isArray(models) && models.length > 0) setAvailableModels(models);
    } catch { /* ignore */ }
    setGlobalSettingsLoading(false);
  }, []);

  const fetchSitemap = useCallback(async () => {
    try {
      const params = sitemapFilter ? `?contentType=${sitemapFilter}` : '';
      const r = await api.get<any>(`/admin/cms/sitemap${params}`);
      setSitemapData(r.data || r);
    } catch { /* ignore */ }
  }, [sitemapFilter]);

  const fetchCosts = useCallback(async () => {
    try {
      const r = await api.get<any>(`/admin/cms/costs?period=${costsPeriod}&limit=200`);
      setCostsList(r.data?.costs || []);
      setCostsSummary(r.data?.summary || null);
    } catch { /* ignore */ }
  }, [costsPeriod]);

  const fetchLocales = useCallback(async () => {
    setLocalesLoading(true);
    try {
      const r = await api.get<any>('/admin/cms/locales/all');
      setAllLocales(r?.locales || r?.data?.locales || []);
    } catch { /* ignore */ }
    setLocalesLoading(false);
  }, []);

  const toggleLocale = async (code: string) => {
    try {
      await api.put(`/admin/cms/locales/${code}/toggle`);
      setAllLocales(prev => prev.map(l => l.code === code ? { ...l, is_active: l.is_active ? 0 : 1 } : l));
    } catch { /* ignore */ }
  };

  const bulkUpdateLocales = async (codes: string[], isActive: 0 | 1) => {
    try {
      await api.put('/admin/cms/locales/bulk', { codes, is_active: isActive });
      setAllLocales(prev => prev.map(l => codes.includes(l.code) ? { ...l, is_active: isActive } : l));
    } catch { /* ignore */ }
  };

  const TOP_20_LOCALES = ['en', 'es', 'fr', 'de', 'pt-BR', 'pt', 'it', 'nl', 'ru', 'ja', 'ko', 'zh', 'zh-TW', 'ar', 'hi', 'tr', 'pl', 'sv', 'da', 'fi'];

  const fetchMonitoring = useCallback(async () => {
    setMonitoringLoading(true);
    try {
      const [statsR, recentR] = await Promise.all([
        api.get<any>(`/admin/cms/monitoring/stats?period=${monitoringPeriod}`),
        api.get<any>('/admin/cms/monitoring/recent?limit=50'),
      ]);
      setMonitoringStats(statsR.data || null);
      setMonitoringRecent(recentR.data?.operations || []);
    } catch { /* ignore */ }
    setMonitoringLoading(false);
  }, [monitoringPeriod]);

  const fetchScheduling = useCallback(async () => {
    setSchedLoading(true);
    try {
      const [rulesR, queueR, calR, histR] = await Promise.all([
        api.get<any>('/admin/cms/schedule/rules'),
        api.get<any>('/admin/cms/schedule/queue'),
        api.get<any>(`/admin/cms/schedule/calendar?month=${schedCalMonth}`),
        api.get<any>(`/admin/cms/schedule/history?page=${schedHistoryPage}&limit=20${schedHistoryFilter ? '&status=' + schedHistoryFilter : ''}`),
      ]);
      setSchedRules(rulesR.data || []);
      setSchedQueue(queueR.data || []);
      setSchedCalendar((calR.data as any)?.days || {});
      setSchedHistory((histR.data as any)?.items || []);
      setSchedHistoryTotal((histR.data as any)?.total || 0);
    } catch { /* ignore */ }
    setSchedLoading(false);
  }, [schedCalMonth, schedHistoryPage, schedHistoryFilter]);

  const fetchEntryCosts = useCallback(async (entryId: string) => {
    try {
      const r = await api.get<any>(`/admin/cms/entries/${entryId}/costs`);
      setEntryCosts(r.data || null);
    } catch { setEntryCosts(null); }
  }, []);

  // ── Effects ──
  const fetchModels = useCallback(async () => {
    try {
      const r = await api.get<any>('/admin/cms/models');
      const models = r.data?.models || r.models || r || [];
      if (Array.isArray(models) && models.length) setAvailableModels(models);
    } catch { /* use defaults */ }
  }, []);

  const translateEntry = async (entryId: string, targetLocales: string[] | 'all') => {
    const activeLocales = allLocales.filter(l => l.is_active && l.code !== 'pt-BR');
    if (targetLocales === 'all' && activeLocales.length === 0) {
      alert('Nenhum idioma ativo. Va na aba "Idiomas" e ative os idiomas desejados primeiro.');
      return;
    }
    if (targetLocales === 'all' && activeLocales.length > 30) {
      const ok = confirm(`Voce tem ${activeLocales.length} idiomas ativos. Traduzir para todos pode demorar muito e consumir creditos.\n\nRecomendacao: Use "Apenas Principais (20)" na aba Idiomas.\n\nDeseja continuar mesmo assim?`);
      if (!ok) return;
    }
    setTranslating(true);
    setTranslateResult(null);
    let totalTranslated = 0, totalFailed = 0;
    const allFailedLocales: string[] = [];
    try {
      // Loop until no remaining (backend returns partial results with remaining[])
      let hasRemaining = true;
      let loopCount = 0;
      while (hasRemaining && loopCount < 100) {
        loopCount++;
        const r = await api.post<any>(`/admin/cms/entries/${entryId}/translate`, {
          source_locale: fLocale || 'pt-BR',
          target_locales: targetLocales,
          model: translateModel,
        }, { timeout: 120000 });
        const data = r.data || r;
        totalTranslated += data.translated || 0;
        totalFailed += data.failed || 0;
        if (data.failed_locales?.length) allFailedLocales.push(...data.failed_locales);
        const rem = data.remaining?.length || 0;
        hasRemaining = rem > 0;
        setTranslateResult({ translated: totalTranslated, failed: totalFailed, total: totalTranslated + totalFailed + rem, failed_locales: allFailedLocales });
        // If nothing was translated this round and nothing remaining, stop
        if ((data.translated || 0) === 0 && !hasRemaining) break;
      }
      fetchEntries();
    } catch (err: any) {
      totalFailed++;
      allFailedLocales.push(err.message || 'error');
      setTranslateResult({ translated: totalTranslated, failed: totalFailed, total: totalTranslated + totalFailed, failed_locales: allFailedLocales });
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => { fetchTypes(); fetchModels(); }, []);

  useEffect(() => {
    if (!selectedType && !showAll) return;
    setEntriesPage(0);
    setSelectedIds([]);
    setFilterStatus('');
    setFilterCategory('');
    setFilterSearch('');
    if (selectedType) fetchCategories();
  }, [selectedType, showAll]);

  useEffect(() => {
    if (!selectedType && !showAll) return;
    if (subTab === 'entries') fetchEntries();
    else if (subTab === 'categories' && selectedType) fetchCategories();
    else if (subTab === 'analytics' && selectedType) fetchAnalytics();
    else if (subTab === 'menus' && selectedType) fetchMenus();
    else if (subTab === 'settings' && selectedType) fetchSettings();
    else if (subTab === 'audit') fetchAudit();
    else if (subTab === 'users') fetchCmsUsers();
    else if (subTab === 'media') fetchMedia();
    else if (subTab === 'global-config') fetchGlobalSettings();
    else if (subTab === 'sitemap') fetchSitemap();
    else if (subTab === 'costs') fetchCosts();
    else if (subTab === 'languages') fetchLocales();
    else if (subTab === 'monitoring') fetchMonitoring();
    else if (subTab === 'scheduling') fetchScheduling();
  }, [subTab, selectedType, showAll, entriesPage, filterStatus, filterCategory, filterSearch, analyticsDays, auditDays, mediaFolder, mediaSearch, sitemapFilter, costsPeriod, monitoringPeriod, fetchScheduling, schedCalMonth, schedHistoryPage, schedHistoryFilter]);

  // ── Entry CRUD ──
  const openNewEntry = () => {
    setEditEntry(null);
    setFSlug(''); setFCatId(categories[0]?.id || ''); setFCover('');
    setFAuthor(''); setFTags(''); setFFeatured(false); setFScheduled('');
    setFLocale('pt-BR');
    setFCodeBlocks([]);
    setFTranslations({
      'pt-BR': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
      'en': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
    });
    setShowEntryModal(true);
  };

  const openEditEntry = async (e: Entry) => {
    try {
      const entry = await api.get<any>(`/admin/cms/entries/${e.id}`);
      setEditEntry(entry);
      setFSlug(entry.slug); setFCatId(entry.category_id || ''); setFCover(entry.cover_image || '');
      setFAuthor(entry.author_name || ''); setFTags(parseTags(entry.tags).join(', '));
      setFFeatured(!!entry.is_featured); setFScheduled(entry.scheduled_at || '');
      try { setFCodeBlocks(JSON.parse(entry.code_blocks || '[]')); } catch { setFCodeBlocks([]); }
      const trans: Record<string, any> = {
        'pt-BR': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
        'en': { title: '', excerpt: '', body: '', meta_title: '', meta_description: '', custom_fields: {} },
      };
      for (const t of (entry.translations || [])) {
        const cf = t.custom_fields ? (typeof t.custom_fields === 'string' ? JSON.parse(t.custom_fields || '{}') : t.custom_fields) : {};
        trans[t.locale] = {
          title: t.title || '', excerpt: t.excerpt || '', body: t.body || '',
          meta_title: t.meta_title || '', meta_description: t.meta_description || '',
          custom_fields: cf,
        };
      }
      setFTranslations(trans);
      setFLocale(entry.translations?.[0]?.locale || 'pt-BR');
      setShowEntryCostDetails(false);
      fetchEntryCosts(e.id);
      setShowEntryModal(true);
    } catch (err) {
      console.error('Failed to fetch entry:', err);
      setEditEntry(e);
      setShowEntryModal(true);
    }
  };

  const updateTransField = (locale: string, field: string, value: any) => {
    setFTranslations(prev => ({
      ...prev,
      [locale]: { ...prev[locale], [field]: value },
    }));
  };

  const updateCustomField = (locale: string, key: string, value: any) => {
    setFTranslations(prev => ({
      ...prev,
      [locale]: { ...prev[locale], custom_fields: { ...(prev[locale]?.custom_fields || {}), [key]: value } },
    }));
  };

  const saveEntry = async (publish: boolean) => {
    if (!selectedType) return;
    setSaving(true);
    const tagsArr = fTags.split(',').map(t => t.trim()).filter(Boolean);
    const translations = Object.entries(fTranslations)
      .filter(([_, v]) => v.title)
      .map(([locale, v]) => ({
        locale, title: v.title, excerpt: v.excerpt, body: v.body,
        custom_fields: JSON.stringify(v.custom_fields || {}),
        meta_title: v.meta_title, meta_description: v.meta_description,
      }));
    const body: any = {
      content_type_id: selectedType.id,
      slug: fSlug || slugify(fTranslations['pt-BR']?.title || fTranslations['en']?.title || ''),
      category_id: fCatId || null,
      cover_image: fCover,
      author_name: fAuthor,
      tags: JSON.stringify(tagsArr),
      is_featured: fFeatured ? 1 : 0,
      status: publish ? 'published' : 'draft',
      scheduled_at: fScheduled || null,
      code_blocks: fCodeBlocks.length > 0 ? JSON.stringify(fCodeBlocks) : null,
      translations,
    };
    try {
      if (editEntry) {
        await api.put(`/admin/cms/entries/${editEntry.id}`, body);
      } else {
        await api.post('/admin/cms/entries', body);
      }
      setShowEntryModal(false);
      fetchEntries();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const publishEntry = async (e: Entry) => {
    await api.post(`/admin/cms/entries/${e.id}/publish`);
    fetchEntries();
  };

  const unpublishEntry = async (e: Entry) => {
    await api.post(`/admin/cms/entries/${e.id}/unpublish`);
    fetchEntries();
  };

  const deleteEntry = async (e: Entry) => {
    const t = e.translations?.find(t => t.locale === 'pt-BR') || e.translations?.[0];
    if (!confirm(`Excluir "${t?.title || e.slug}"?`)) return;
    await api.delete(`/admin/cms/entries/${e.id}`);
    fetchEntries();
  };

  const bulkAction = async (action: 'publish' | 'unpublish' | 'delete') => {
    if (selectedIds.length === 0) return;
    if (action === 'delete' && !confirm(`Excluir ${selectedIds.length} itens?`)) return;
    await api.post('/admin/cms/entries/bulk', { ids: selectedIds, action });
    setSelectedIds([]);
    fetchEntries();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const list = showAll ? allEntriesRaw : entries;
    if (selectedIds.length === list.length) setSelectedIds([]);
    else setSelectedIds(list.map(e => e.id));
  };

  // ── Category CRUD ──
  const openNewCat = () => {
    setEditCat(null);
    setCfSlug(''); setCfColor(T.primary); setCfIcon(''); setCfSortOrder(0); setCfLocale('pt-BR');
    setCfTranslations({ 'pt-BR': { name: '', description: '' }, 'en': { name: '', description: '' } });
    setShowCatModal(true);
  };

  const openEditCat = (c: Category) => {
    setEditCat(c);
    setCfSlug(c.slug); setCfColor(c.color || T.primary); setCfIcon(c.icon || '');
    setCfSortOrder(c.sort_order || 0); setCfLocale('pt-BR');
    const trans: Record<string, any> = { 'pt-BR': { name: '', description: '' }, 'en': { name: '', description: '' } };
    for (const t of c.translations || []) {
      trans[t.locale] = { name: t.name || '', description: t.description || '' };
    }
    setCfTranslations(trans);
    setShowCatModal(true);
  };

  const updateCatTransField = (locale: string, field: string, value: string) => {
    setCfTranslations(prev => ({ ...prev, [locale]: { ...prev[locale], [field]: value } }));
  };

  const saveCat = async () => {
    if (!selectedType) return;
    setSaving(true);
    const translations = Object.entries(cfTranslations)
      .filter(([_, v]) => v.name)
      .map(([locale, v]) => ({ locale, name: v.name, description: v.description }));
    const body: any = {
      content_type_id: selectedType.id,
      slug: cfSlug || slugify(cfTranslations['pt-BR']?.name || cfTranslations['en']?.name || ''),
      color: cfColor, icon: cfIcon, sort_order: cfSortOrder, translations,
    };
    try {
      if (editCat) {
        await api.put(`/admin/cms/categories/${editCat.id}`, body);
      } else {
        await api.post('/admin/cms/categories', body);
      }
      setShowCatModal(false);
      fetchCategories();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const deleteCat = async (c: Category) => {
    const name = c.translations?.[0]?.name || c.slug;
    if (!confirm(`Excluir categoria "${name}"?`)) return;
    await api.delete(`/admin/cms/categories/${c.id}`);
    fetchCategories();
  };

  const moveCatUp = async (idx: number) => {
    if (idx === 0) return;
    const items = [...categories];
    [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
    const reorder = items.map((c, i) => ({ id: c.id, sort_order: i }));
    await api.post('/admin/cms/categories/reorder', { items: reorder });
    fetchCategories();
  };

  const moveCatDown = async (idx: number) => {
    if (idx >= categories.length - 1) return;
    const items = [...categories];
    [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
    const reorder = items.map((c, i) => ({ id: c.id, sort_order: i }));
    await api.post('/admin/cms/categories/reorder', { items: reorder });
    fetchCategories();
  };

  const [catTranslatingIds, setCatTranslatingIds] = useState<Record<string, boolean>>({});
  const translateCategory = async (catId: string, targetLocales: string[] | 'all') => {
    // Check active locales count — warn if translating to all with many active
    const activeLocales = allLocales.filter(l => l.is_active && l.code !== 'pt-BR');
    if (targetLocales === 'all' && activeLocales.length === 0) {
      alert('Nenhum idioma ativo. Va na aba "Idiomas" e ative os idiomas desejados primeiro.');
      return;
    }
    if (targetLocales === 'all' && activeLocales.length > 30) {
      const ok = confirm(`Voce tem ${activeLocales.length} idiomas ativos. Traduzir para todos pode demorar muito e consumir creditos.\n\nRecomendacao: Use "Apenas Principais (20)" na aba Idiomas.\n\nDeseja continuar mesmo assim?`);
      if (!ok) return;
    }
    setCatTranslating(true);
    setCatTranslatingIds(prev => ({ ...prev, [catId]: true }));
    setCatTranslateResult(null);
    let totalTranslated = 0, totalFailed = 0, totalCount = 0;
    const allFailedLocales: string[] = [];
    try {
      // Loop until no remaining (backend caps at 5 per request)
      let hasRemaining = true;
      let loopCount = 0;
      while (hasRemaining && loopCount < 100) {
        loopCount++;
        const r = await api.post<any>(`/admin/cms/categories/${catId}/translate`, {
          source_locale: 'pt-BR',
          target_locales: targetLocales,
          translate_slug: true,
        }, { timeout: 120000 });
        const data = r.data || r;
        totalTranslated += data.translated || 0;
        totalFailed += data.failed || 0;
        totalCount += data.total || 0;
        if (data.failed_locales?.length) allFailedLocales.push(...data.failed_locales);
        hasRemaining = (data.remaining || 0) > 0;
        setCatTranslateResult({ translated: totalTranslated, failed: totalFailed, total: totalCount + (data.remaining || 0), failed_locales: allFailedLocales });
        if ((data.translated || 0) === 0 && !hasRemaining) break;
      }
      fetchCategories();
    } catch (err: any) {
      totalFailed++;
      allFailedLocales.push(err.message || 'error');
      setCatTranslateResult({ translated: totalTranslated, failed: totalFailed, total: totalCount || 1, failed_locales: allFailedLocales });
    }
    setCatTranslatingIds(prev => { const n = { ...prev }; delete n[catId]; return n; });
    setCatTranslating(false);
  };
  const translateAllCategories = async () => {
    const activeLocales = allLocales.filter(l => l.is_active && l.code !== 'pt-BR');
    if (activeLocales.length === 0) {
      alert('Nenhum idioma ativo. Va na aba "Idiomas" e ative os idiomas desejados primeiro.');
      return;
    }
    if (activeLocales.length > 30) {
      const ok = confirm(`Voce tem ${activeLocales.length} idiomas ativos. Traduzir todas as categorias para todos pode demorar muito.\n\nDeseja continuar?`);
      if (!ok) return;
    }
    setCatTranslating(true);
    for (const c of categories) {
      setCatTranslatingIds(prev => ({ ...prev, [c.id]: true }));
      try {
        await api.post<any>(`/admin/cms/categories/${c.id}/translate`, {
          source_locale: 'pt-BR',
          target_locales: 'all',
          translate_slug: true,
        });
      } catch {}
      setCatTranslatingIds(prev => { const n = { ...prev }; delete n[c.id]; return n; });
    }
    fetchCategories();
    setCatTranslating(false);
  };

  // ── Menu CRUD ──
  const openNewMenu = () => {
    setEditMenu(null);
    setMfSlug(''); setMfLocation('header'); setMfItemsJson('[]');
    setShowMenuModal(true);
  };

  const openEditMenu = (m: Menu) => {
    setEditMenu(m);
    setMfSlug(m.slug); setMfLocation(m.location);
    setMfItemsJson(JSON.stringify(m.items_json || [], null, 2));
    setShowMenuModal(true);
  };

  const saveMenu = async () => {
    setSaving(true);
    let itemsParsed: any[] = [];
    try { itemsParsed = JSON.parse(mfItemsJson); } catch { /* ignore */ }
    const body: any = {
      content_type_id: selectedType?.id,
      slug: mfSlug, location: mfLocation, items_json: itemsParsed,
    };
    try {
      if (editMenu) {
        await api.put(`/admin/cms/menus/${editMenu.id}`, body);
      } else {
        await api.post('/admin/cms/menus', body);
      }
      setShowMenuModal(false);
      fetchMenus();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const deleteMenu = async (m: Menu) => {
    if (!confirm(`Excluir menu "${m.slug}"?`)) return;
    await api.delete(`/admin/cms/menus/${m.id}`);
    fetchMenus();
  };

  // ── Settings ──
  const saveSettings = async () => {
    if (!selectedType) return;
    setSaving(true);
    // Merge existing settings with edited values
    const merged: Record<string, string> = {};
    for (const s of settings) merged[s.setting_key] = s.setting_value;
    for (const [k, v] of Object.entries(settingsEdited)) merged[k] = v;
    if (newSettingKey) merged[newSettingKey] = newSettingVal;
    const settingsArr = Object.entries(merged).map(([key, value]) => ({ key, value }));
    try {
      await api.put('/admin/cms/settings', { content_type_id: selectedType.id, settings: settingsArr });
      setNewSettingKey(''); setNewSettingVal('');
      fetchSettings();
    } catch { /* ignore */ }
    setSaving(false);
  };

  // ── CMS User CRUD ──
  const saveCmsUser = async () => {
    if (!newUserCustomerId) return;
    setSaving(true);
    try {
      await api.post('/admin/cms/users', { customer_id: newUserCustomerId, role: newUserRole, email: newUserEmail, display_name: newUserName });
      setShowUserModal(false);
      setNewUserCustomerId(''); setNewUserEmail(''); setNewUserName(''); setNewUserRole('editor');
      fetchCmsUsers();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const updateCmsUserRole = async (userId: string, role: string) => {
    await api.put(`/admin/cms/users/${userId}`, { role });
    fetchCmsUsers();
  };

  const deleteCmsUser = async (userId: string) => {
    if (!confirm('Remover este usuario do CMS?')) return;
    await api.delete(`/admin/cms/users/${userId}`);
    fetchCmsUsers();
  };

  // ── Media CRUD ──
  const addMediaUrl = async () => {
    if (!newMediaUrl) return;
    setSaving(true);
    try {
      await api.post('/admin/cms/media/url', { url: newMediaUrl, alt_text: newMediaAlt, folder: newMediaFolder });
      setShowMediaUrlModal(false);
      setNewMediaUrl(''); setNewMediaAlt(''); setNewMediaFolder('');
      fetchMedia();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const updateMediaAlt = async (mediaId: string, altText: string) => {
    await api.put(`/admin/cms/media/${mediaId}`, { alt_text: altText });
  };

  const deleteMedia = async (mediaId: string) => {
    if (!confirm('Excluir esta midia?')) return;
    await api.delete(`/admin/cms/media/${mediaId}`);
    fetchMedia();
  };

  // ── Global Settings Save ──
  const saveGlobalSettings = async () => {
    setSaving(true);
    const settingsArr = Object.entries(globalSettings).map(([key, value]) => ({ key, value }));
    try {
      await api.put('/admin/cms/global-settings', { settings: settingsArr });
    } catch { /* ignore */ }
    setSaving(false);
  };

  const updateGlobalSetting = (key: string, value: string) => {
    setGlobalSettings(prev => {
      const next = { ...prev, [key]: value };
      // Auto-save model/provider changes immediately
      if (key.startsWith('llm_') && (key.includes('_model') || key.includes('_provider'))) {
        const settingsArr = Object.entries(next).map(([k, v]) => ({ key: k, value: v }));
        api.put('/admin/cms/global-settings', { settings: settingsArr }).catch(() => {});
      }
      return next;
    });
  };

  const getModelsForProvider = (provider: string): string[] => {
    let models = availableModels;
    if (provider) {
      models = availableModels.filter(m => m.startsWith(provider + '/') || m.toLowerCase().includes(provider.toLowerCase()));
    }
    return models;
  };

  // Fetch models dynamically for a specific provider (elon vs direct)
  const [modelsFetchStatus, setModelsFetchStatus] = useState<{ url?: string; count?: number; error?: string }>({});

  const fetchModelsForOp = async (provider: string, force?: boolean) => {
    if (!provider) return;
    if (!force && opModelsCache[provider]?.length) return; // use cache unless forced
    setOpModelsLoading(prev => ({ ...prev, [provider]: true }));
    setModelsFetchStatus({});
    try {
      let models: string[] = [];
      if (provider === 'elon') {
        // Fetch from Elon Router using token
        const token = globalSettings.llm_api_token || globalSettings.llm_token || '';
        if (!token) {
          setModelsFetchStatus({ error: 'Token nao configurado. Preencha o token no Passo 3 e clique Atualizar.' });
          setOpModelsLoading(prev => ({ ...prev, [provider]: false }));
          return;
        }
        let baseUrl = (globalSettings.llm_provider_url || '/api/v1/llm/v1').replace(/\/$/, '');
        // Ensure URL ends with /v1 for OpenAI-compatible endpoint
        if (!baseUrl.endsWith('/v1')) baseUrl += '/v1';
        const fetchUrl = `${baseUrl}/models`;
        setModelsFetchStatus({ url: fetchUrl });
        const r = await fetch(fetchUrl, { headers: { 'Authorization': `Bearer ${token}` } });
        const json = await r.json() as any;
        const data = json.data || json.models || json;
        if (Array.isArray(data)) {
          models = data.map((m: any) => typeof m === 'string' ? m : m.id || m.model || '').filter(Boolean);
        }
        setModelsFetchStatus({ url: fetchUrl, count: models.length });
      } else {
        // Fetch from admin CMS endpoint filtered by provider
        const fetchUrl = `/admin/cms/models?provider=${encodeURIComponent(provider)}`;
        setModelsFetchStatus({ url: fetchUrl });
        const r = await api.get<any>(fetchUrl);
        models = r.data?.models || r.models || [];
        setModelsFetchStatus({ url: fetchUrl, count: Array.isArray(models) ? models.length : 0 });
      }
      setOpModelsCache(prev => ({ ...prev, [provider]: Array.isArray(models) ? models : [] }));
    } catch (err: any) {
      setModelsFetchStatus(prev => ({ ...prev, error: err?.message || 'Erro ao buscar modelos' }));
    }
    setOpModelsLoading(prev => ({ ...prev, [provider]: false }));
  };

  // Ensure current value is in the list (so dropdown shows it even if format differs)
  const getModelsWithCurrent = (provider: string, currentValue: string): string[] => {
    const models = getModelsForProvider(provider);
    if (currentValue && !models.includes(currentValue)) {
      // Try to find matching model (e.g., "gpt-4o-mini" matches "openai/gpt-4o-mini")
      const match = models.find(m => m.endsWith('/' + currentValue) || m === currentValue);
      if (!match) return [currentValue, ...models]; // Add current value to list so it shows
    }
    return models;
  };

  // Default model labels for each operation
  const DEFAULT_MODELS: Record<string, string> = {
    llm_translation_model: 'openai/gpt-4o-mini',
    llm_image_model: 'openai/gpt-image-1',
    llm_summary_model: 'openai/gpt-4o-mini',
    llm_restyle_model: 'anthropic/claude-sonnet-4-5',
    llm_content_model: 'openai/gpt-4o-mini',
    llm_idea_model: 'openai/gpt-4o-mini',
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);
    try {
      const token = globalSettings.llm_api_token || globalSettings.llm_token || '';
      let baseUrl = (globalSettings.llm_provider_url || '/api/v1/llm').replace(/\/$/, '');
      if (!baseUrl.endsWith('/v1')) baseUrl += '/v1';
      if (!token) {
        setConnectionTestResult({ ok: false, message: 'Token nao configurado. Preencha no Passo 3.' });
        setTestingConnection(false);
        return;
      }
      const r = await fetch(`${baseUrl}/models`, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await r.json() as any;
      const data = json.data || json.models || json;
      if (Array.isArray(data) && data.length > 0) {
        const models = data.map((m: any) => typeof m === 'string' ? m : m.id || m.model || '').filter(Boolean);
        setAvailableModels(models);
        setOpModelsCache(prev => ({ ...prev, elon: models }));
        setConnectionTestResult({ ok: true, message: `Conectado - ${models.length} modelos disponiveis` });
      } else {
        setConnectionTestResult({ ok: false, message: 'Conexao OK mas nenhum modelo encontrado' });
      }
    } catch (err: any) {
      setConnectionTestResult({ ok: false, message: err.message || 'Erro ao conectar' });
    }
    setTestingConnection(false);
  };

  // ── Markdown toolbar insert ──
  const insertMarkdown = (prefix: string, suffix: string, placeholder: string) => {
    const textarea = document.querySelector('textarea[data-md-editor]') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const val = textarea.value;
    const selected = val.substring(start, end);
    const insert = selected ? `${prefix}${selected}${suffix}` : `${prefix}${placeholder}${suffix}`;
    const newVal = val.substring(0, start) + insert + val.substring(end);
    updateTransField(fLocale, 'body', newVal);
    setTimeout(() => {
      textarea.focus();
      const cursorPos = selected ? start + insert.length : start + prefix.length;
      const cursorEnd = selected ? start + insert.length : start + prefix.length + placeholder.length;
      textarea.setSelectionRange(cursorPos, cursorEnd);
    }, 0);
  };

  // ── Helpers ──
  const getEntryTitle = (e: Entry) => {
    const t = e.translations?.find(t => t.locale === 'pt-BR') || e.translations?.[0];
    return t?.title || e.slug;
  };

  const getCatName = (catId: string) => {
    const c = categories.find(c => c.id === catId);
    return c?.translations?.[0]?.name || c?.slug || '-';
  };

  const getCatColor = (catId: string) => {
    const c = categories.find(c => c.id === catId);
    return c?.color || T.textMuted;
  };

  const totalPages = Math.ceil(entriesTotal / 20);
  const customFields = selectedType ? parseCustomFields(selectedType.custom_fields_schema) : [];
  const typeColor = selectedType?.color || T.primary;

  // Sub-tabs available for this type
  const availableSubTabs: { key: string; label: string }[] = [
    { key: 'entries', label: 'Conteudo' },
    { key: 'categories', label: 'Categorias' },
    { key: 'media', label: 'Midia' },
    { key: 'analytics', label: 'Estatisticas' },
    { key: 'menus', label: 'Menus' },
    { key: 'users', label: 'Usuarios' },
    { key: 'settings', label: 'Configuracoes' },
    { key: 'audit', label: 'Historico' },
    { key: 'sitemap', label: 'Sitemap' },
    { key: 'costs', label: 'Custos' },
    { key: 'languages', label: 'Idiomas' },
    { key: 'monitoring', label: 'Monitoramento' },
    { key: 'scheduling', label: 'Agendamento' },
    { key: 'llm-test', label: 'Teste LLM' },
  ];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: T.text, letterSpacing: '-0.02em' }}>CMS</h1>
          <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>Gerencie conteudo, categorias e configuracoes</p>
        </div>
        <Button variant="ghost" onClick={() => setSubTab('global-config')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          Configuracoes
        </Button>
      </div>

      {/* Content Type Selector (pill bar) */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
        <Button variant="ghost" onClick={() => { setShowAll(true); setSelectedType(null); setSubTab('entries'); }}>
          <span>Todos</span>
          <span style={{ fontSize: 10, fontWeight: 600, background: `${T.purple}18`, color: T.purple, padding: '1px 5px', borderRadius: 3, minWidth: 18, textAlign: 'center' as const }}>{typeCounts['__all__'] || 0}</span>
        </Button>
        {types.map(t => {
          const friendlyNames: Record<string, string> = {
            'blog': 'Blog', 'help': 'Help Center', 'changelog': 'Changelog',
            'dev-docs': 'Developers', 'solutions': 'Solutions',
            'integrations': 'Integracoes', 'marketplace': 'Marketplace',
          };
          const displayName = friendlyNames[t.slug] || t.label_plural || t.label;
          const isActive = !showAll && selectedType?.id === t.id;
          const count = typeCounts[t.id] || 0;
          return (
            <Button variant="ghost" onClick={() => { setShowAll(false); setSelectedType(t); setSubTab('entries'); }}>
              <span>{displayName}</span>
              <span style={{ fontSize: 10, fontWeight: 600, background: `${t.color || T.primary}18`, color: t.color || T.primary, padding: '1px 5px', borderRadius: 3, minWidth: 18, textAlign: 'center' as const }}>{count}</span>
            </Button>
          );
        })}
      </div>

      {/* Sub-tabs */}
      {selectedType && !showAll && (
        <div style={S.tabs}>
          {availableSubTabs.map(st => (
            <Button variant="ghost" onClick={() => setSubTab(st.key as any)}>
              {st.label}
            </Button>
          ))}
        </div>
      )}

      {!selectedType && !showAll && (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      )}

      {/* ═══ ENTRIES TAB ═══ */}
      {(selectedType || showAll) && subTab === 'entries' && (() => {
        const displayEntries = showAll ? allEntriesRaw : entries;
        const displayTotal = showAll ? allEntriesTotal : entriesTotal;
        const displayPages = Math.ceil(displayTotal / 20);
        const hasCats = showAll ? true : !!selectedType?.has_categories;
        const getTypeName = (ctId: string) => {
          const ct = types.find(t => t.id === ctId);
          return ct?.label || ctId;
        };
        const getTypeColor = (ctId: string) => {
          const ct = types.find(t => t.id === ctId);
          return ct?.color || T.textMuted;
        };

        // Sort entries
        const sortedEntries = [...displayEntries].sort((a, b) => {
          if (sortBy === 'title') return getEntryTitle(a).localeCompare(getEntryTitle(b));
          if (sortBy === 'views') return (b.view_count || 0) - (a.view_count || 0);
          if (sortBy === 'seo') {
            const sa = calcSeoScore(a, a.translations?.find(t => t.locale === 'pt-BR') || a.translations?.[0]);
            const sb = calcSeoScore(b, b.translations?.find(t => t.locale === 'pt-BR') || b.translations?.[0]);
            return sb - sa;
          }
          // date (default)
          return new Date(b.published_at || b.created_at).getTime() - new Date(a.published_at || a.created_at).getTime();
        });

        // Stats
        const publishedCount = displayEntries.filter(e => e.status === 'published').length;
        const draftCount = displayEntries.filter(e => e.status === 'draft' || e.status === 'idea').length;
        const totalViews = displayEntries.reduce((sum, e) => sum + (e.view_count || 0), 0);

        // Status badge renderer
        const renderStatusBadge = (status: string) => {
          const config: Record<string, { bg: string; color: string; label: string; dot?: boolean }> = {
            published: { bg: T.successBg, color: T.success, label: 'Publicado', dot: true },
            draft: { bg: T.warningBg, color: T.warning, label: 'Rascunho' },
            idea: { bg: 'rgba(148,163,184,0.12)', color: T.textSecondary, label: 'Ideia' },
            scheduled: { bg: T.accentBg, color: T.accent, label: 'Agendado' },
          };
          const c = config[status] || config.draft;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color, border: `1px solid ${c.color}25` }}>
              {c.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, animation: 'pulse 2s infinite', boxShadow: `0 0 6px ${c.color}` }} />}
              {c.label}
            </span>
          );
        };

        // SEO circle
        const renderSeoCircle = (score: number) => {
          const color = seoColor(score);
          const circumference = 2 * Math.PI * 14;
          const offset = circumference - (score / 100) * circumference;
          return (
            <div style={{ position: 'relative' as const, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="36" height="36" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="14" fill="none" stroke={T.borderHover} strokeWidth="3" />
                <circle cx="18" cy="18" r="14" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${circumference}`} strokeDashoffset={`${offset}`} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
              </svg>
              <span style={{ position: 'absolute' as const, fontSize: 9, fontWeight: 700, color }}>{score}</span>
            </div>
          );
        };

        // Action menu for cards
        const renderActionMenu = (e: Entry) => {
          const menuRef = (el: HTMLButtonElement | null) => {
            if (el && actionMenuId === e.id) {
              const menu = el.nextElementSibling as HTMLElement;
              if (menu) {
                const rect = el.getBoundingClientRect();
                menu.style.position = 'fixed';
                menu.style.top = `${rect.bottom + 4}px`;
                menu.style.left = `${rect.right - 180}px`;
              }
            }
          };
          return (
          <div style={{ position: 'relative' as const, zIndex: actionMenuId === e.id ? 9999 : 10 }}>
            <Button variant="ghost" size="xs" onClick={(ev: any) => { ev.stopPropagation(); setActionMenuId(actionMenuId === e.id ? null : e.id); }}>⋯</Button>
            {actionMenuId === e.id && (
              <div style={{ position: 'fixed' as const, background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 10, padding: 4, minWidth: 180, boxShadow: '0 12px 40px rgba(0,0,0,0.7)', zIndex: 99999 }} onClick={(ev: any) => ev.stopPropagation()}>
                <Button variant="ghost" fullWidth onClick={() => { setActionMenuId(null); openEditEntry(e); }}>Editar</Button>
                <Button variant="ghost" fullWidth onClick={() => { setActionMenuId(null); translateEntry(e.id, 'all'); }} disabled={translating}>Traduzir</Button>
                <Button variant="ghost" fullWidth onClick={async () => { setActionMenuId(null); setGeneratingImage(true); try { await api.post(`/admin/cms/entries/${e.id}/generate-image`, { type: 'cover' }); fetchEntries(); } catch { /* */ } setGeneratingImage(false); }} disabled={generatingImage}>{e.cover_image ? 'Regen Capa' : 'Gerar Capa'}</Button>
                <div style={{ height: 1, background: T.borderHover, margin: '4px 0' }} />
                {e.status === 'published' ? (
                  <Button variant="ghost" fullWidth onClick={() => { setActionMenuId(null); unpublishEntry(e); }}>Despublicar</Button>
                ) : (
                  <Button variant="ghost" fullWidth onClick={() => { setActionMenuId(null); publishEntry(e); }}>Publicar</Button>
                )}
                <Button variant="ghost" fullWidth onClick={() => { setActionMenuId(null); deleteEntry(e); }}>Excluir</Button>
              </div>
            )}
          </div>
          );
        };

        return (
        <div onClick={() => setActionMenuId(null)}>
          {/* Animations + mobile scrollbar hide */}
          <style>{`
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .cms-scroll-hide::-webkit-scrollbar { display: none; }
          `}</style>

          {/* Action buttons */}
          {selectedType && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
              <Button variant="ghost" onClick={openNewEntry}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                Novo Conteudo
              </Button>
              <Button variant="ghost" onClick={async () => {
                  setGeneratingIdea(true);
                  try {
                    const r = await api.post<any>('/admin/cms/entries/generate-idea', { content_type_id: selectedType?.id || 'blog' });
                    const data = r.data || r;
                    if (data.title) {
                      fetchEntries();
                      alert('Ideia gerada: ' + data.title);
                    }
                  } catch (err: any) {
                    alert('Erro: ' + (err.message || 'Erro desconhecido'));
                  }
                  setGeneratingIdea(false);
                }} disabled={generatingIdea}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                {generatingIdea ? 'Gerando...' : 'Gerar Nova Ideia'}
              </Button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total', value: displayTotal, color: T.primary },
              { label: 'Publicados', value: publishedCount, color: T.success },
              { label: 'Rascunhos', value: draftCount, color: T.warning },
              { label: 'Views', value: totalViews, color: T.purple },
            ].map(st => (
              <div key={st.label} style={{ background: T.bgOverlay, backdropFilter: 'blur(12px)', border: '1px solid rgba(31,41,55,0.6)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text, lineHeight: 1 }}>{typeof st.value === 'number' && st.value > 999 ? formatCount(st.value) : st.value}</div>
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{st.label}</div>
              </div>
            ))}
          </div>

          {/* Search bar */}
          <div style={{ position: 'relative' as const, marginBottom: 16 }}>
            <span style={{ position: 'absolute' as const, left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.textMuted }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </span>
            <input
              style={{ width: '100%', padding: '12px 14px 12px 42px', fontSize: 14, background: T.bgOverlay, backdropFilter: 'blur(8px)', border: `1px solid ${T.borderHover}`, borderRadius: 12, color: T.text, outline: 'none', boxSizing: 'border-box' as const }}
              value={filterSearch}
              onInput={(e: any) => setFilterSearch(e.target.value)}
              placeholder="Buscar conteudo por titulo, slug..."
            />
          </div>

          {/* Filter pills + View toggle + Sort */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
            {/* Status pills */}
            {[
              { value: '', label: 'Todos', color: T.textMuted },
              { value: 'published', label: 'Publicados', color: T.success },
              { value: 'draft', label: 'Rascunhos', color: T.warning },
              { value: 'scheduled', label: 'Agendados', color: T.accent },
            ].map(st => (
              <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(filterStatus === st.value ? '' : st.value); setEntriesPage(0); }}>{st.label}</Button>
            ))}

            {/* Category pills */}
            {!showAll && selectedType?.has_categories && categories.length > 0 && (
              <>
                <div style={{ width: 1, height: 24, background: T.borderHover, margin: '0 4px' }} />
                {categories.slice(0, 6).map(c => {
                  const catColor = c.color || T.textMuted;
                  const catName = c.translations?.[0]?.name || c.slug;
                  const isActive = filterCategory === c.slug;
                  return (
                    <Button variant="ghost" size="sm" onClick={() => { setFilterCategory(isActive ? '' : c.slug); setEntriesPage(0); }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: catColor, marginRight: 6 }} />
                      {catName}
                    </Button>
                  );
                })}
              </>
            )}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Sort */}
            <select
              style={{ padding: '6px 10px', fontSize: 11, background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 8, color: T.textSecondary, outline: 'none', cursor: 'pointer' }}
              value={sortBy}
              onChange={(e: any) => setSortBy(e.target.value)}
            >
              <option value="date">📅 Data</option>
              <option value="title">🔤 Titulo</option>
              <option value="views">Views</option>
              <option value="seo">SEO</option>
            </select>

            {/* View layout toggle */}
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.borderHover}` }}>
              <Button variant="ghost" size="sm" onClick={() => setViewLayout('grid')} title="Grid">⊞</Button>
              <Button variant="ghost"
                style={{ padding: '6px 10px', fontSize: 14, background: viewLayout === 'list' ? T.borderHover : 'transparent', color: viewLayout === 'list' ? T.text : T.textMuted, border: 'none', cursor: 'pointer', borderLeft: `1px solid ${T.borderHover}` }}
                onClick={() => setViewLayout('list')}
                title="Lista"
              >☰</Button>
            </div>

            {/* Clear filters */}
            {(filterStatus || filterCategory || filterSearch) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterCategory(''); setFilterSearch(''); setEntriesPage(0); }}>✕ Limpar filtros</Button>
            )}

            <span style={{ fontSize: 11, color: T.textMuted }}>{displayTotal} resultado{displayTotal !== 1 ? 's' : ''}</span>
          </div>

          {entriesLoading ? (
            <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 12, animation: 'pulse 1.5s infinite' }}></div>
              <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
            </div>
          ) : sortedEntries.length === 0 ? (
            /* Empty state */
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(17,24,39,0.4)', backdropFilter: 'blur(8px)', borderRadius: 16, border: `1px solid ${T.bgHover}60` }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}></div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 8 }}>Nenhum conteudo encontrado</h3>
              <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
                {filterSearch || filterStatus || filterCategory
                  ? 'Tente ajustar os filtros para encontrar o que procura.'
                  : 'Comece criando seu primeiro conteudo. É rápido e fácil!'}
              </p>
              {!filterSearch && !filterStatus && !filterCategory && selectedType && (
                <Button variant="ghost" onClick={openNewEntry}>+ Criar primeiro conteudo</Button>
              )}
            </div>
          ) : viewLayout === 'grid' ? (
            /* ═══ GRID VIEW (Cards) ═══ */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {sortedEntries.map(e => {
                const t0 = e.translations?.find(t => t.locale === 'pt-BR') || e.translations?.[0];
                const seo = calcSeoScore(e, t0);
                const catColor = getCatColor(e.category_id);
                const catName = getCatName(e.category_id);
                const isSelected = selectedIds.includes(e.id);
                return (
                  <div
                    key={e.id}
                    style={{
                      position: 'relative' as const,
                      background: 'rgba(17,24,39,0.5)',
                      backdropFilter: 'blur(12px)',
                      border: isSelected ? `1px solid ${T.primary}80` : '1px solid rgba(31,41,55,0.5)',
                      borderRadius: 14,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      boxShadow: isSelected ? `0 0 0 2px ${T.primaryBgStrong}` : `0 2px 8px ${T.blackAlpha15}`,
                    }}
                    onClick={(ev: any) => { if (!(ev.target as HTMLElement).closest('button') && !(ev.target as HTMLElement).closest('input')) openEditEntry(e); }}
                    onMouseEnter={(ev: any) => { ev.currentTarget.style.transform = 'translateY(-2px)'; ev.currentTarget.style.boxShadow = `0 8px 24px ${T.blackAlpha30}`; }}
                    onMouseLeave={(ev: any) => { ev.currentTarget.style.transform = 'translateY(0)'; ev.currentTarget.style.boxShadow = isSelected ? `0 0 0 2px ${T.primaryBgStrong}` : `0 2px 8px ${T.blackAlpha15}`; }}
                  >
                    {/* Cover image */}
                    {e.cover_image ? (
                      <div style={{ height: 160, overflow: 'hidden', position: 'relative' as const }}>
                        <img src={e.cover_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' as const }} />
                        <div style={{ position: 'absolute' as const, inset: 0, background: `linear-gradient(transparent 50%, ${T.bgOverlay})` }} />
                      </div>
                    ) : (
                      <div style={{ height: 80, background: `linear-gradient(135deg, ${catColor}30, ${catColor}08)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 28, opacity: 0.3 }}>{selectedType?.icon || ''}</span>
                      </div>
                    )}

                    {/* Checkbox */}
                    <div style={{ position: 'absolute' as const, left: 10, top: 10, zIndex: 5 }} onClick={(ev: any) => ev.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(e.id)}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: T.primary }}
                      />
                    </div>

                    {/* Action menu */}
                    {renderActionMenu(e)}

                    {/* Content */}
                    <div style={{ padding: '14px 16px 16px' }}>
                      {/* Category + Status row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' as const }}>
                        {e.category_id && catName !== '-' && (
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: `${catColor}18`, color: catColor, border: `1px solid ${catColor}25` }}>
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: catColor, marginRight: 4 }} />
                            {catName}
                          </span>
                        )}
                        {showAll && (
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 500, background: `${getTypeColor(e.content_type_id)}15`, color: getTypeColor(e.content_type_id) }}>{getTypeName(e.content_type_id)}</span>
                        )}
                        {e.is_featured ? <span style={{ padding: '2px 6px', borderRadius: 12, fontSize: 10, background: T.warningBg, color: T.warning }}></span> : null}
                        <div style={{ flex: 1 }} />
                        {renderStatusBadge(e.status)}
                      </div>

                      {/* Title */}
                      <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 4px', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
                        {getEntryTitle(e)}
                      </h3>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>/{e.slug}</div>

                      {/* Excerpt */}
                      {t0?.excerpt && (
                        <p style={{ fontSize: 12, color: T.textSecondary, margin: '0 0 12px', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
                          {t0.excerpt}
                        </p>
                      )}

                      {/* Bottom row: metrics */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
                        {/* Languages */}
                        <span style={{ fontSize: 11, color: T.textMuted }}>{e.translations?.length || 0}</span>
                        {/* Words */}
                        <span style={{ fontSize: 11, color: T.textMuted }}> {formatCount(t0?.word_count || 0)}</span>
                        {/* Views */}
                        <span style={{ fontSize: 11, color: T.textMuted }}>{formatCount(e.view_count || 0)}</span>
                        <div style={{ flex: 1 }} />
                        {/* SEO circle */}
                        {renderSeoCircle(seo)}
                        {/* Date */}
                        <span style={{ fontSize: 10, color: T.textMuted }}>
                          {e.published_at ? new Date(e.published_at).toLocaleDateString('pt-BR') : new Date(e.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ═══ LIST VIEW (Modern table) ═══ */
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
              {/* Select all */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', fontSize: 11, color: T.textMuted }}>
                <input type="checkbox" checked={selectedIds.length === sortedEntries.length && sortedEntries.length > 0} onChange={toggleSelectAll} style={{ accentColor: T.primary }} />
                <span style={{ width: 56 }}>Capa</span>
                <span style={{ flex: 1 }}>Conteudo</span>
                <span style={{ width: 100, textAlign: 'center' as const }}>Categoria</span>
                <span style={{ width: 100, textAlign: 'center' as const }}>Status</span>
                <span style={{ width: 60, textAlign: 'center' as const }}>Idiomas</span>
                <span style={{ width: 60, textAlign: 'center' as const }}>Views</span>
                <span style={{ width: 50, textAlign: 'center' as const }}>SEO</span>
                <span style={{ width: 80, textAlign: 'center' as const }}>Data</span>
                <span style={{ width: 40 }} />
              </div>
              {sortedEntries.map(e => {
                const t0 = e.translations?.find(t => t.locale === 'pt-BR') || e.translations?.[0];
                const seo = calcSeoScore(e, t0);
                const catColor = getCatColor(e.category_id);
                const catName = getCatName(e.category_id);
                const isSelected = selectedIds.includes(e.id);
                return (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      background: isSelected ? T.primaryBg : T.bgOverlay,
                      backdropFilter: 'blur(8px)',
                      border: isSelected ? `1px solid ${T.primary}40` : '1px solid rgba(31,41,55,0.3)',
                      borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
                      position: 'relative' as const,
                    }}
                    onClick={(ev: any) => { if (!(ev.target as HTMLElement).closest('button') && !(ev.target as HTMLElement).closest('input')) openEditEntry(e); }}
                    onMouseEnter={(ev: any) => { ev.currentTarget.style.background = T.bgOverlay; }}
                    onMouseLeave={(ev: any) => { ev.currentTarget.style.background = isSelected ? T.primaryBg : T.bgOverlay; }}
                  >
                    {/* Checkbox */}
                    <div onClick={(ev: any) => ev.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(e.id)} style={{ accentColor: T.primary }} />
                    </div>
                    {/* Cover */}
                    {e.cover_image ? (
                      <img src={e.cover_image} alt="" style={{ width: 56, height: 40, objectFit: 'cover' as const, borderRadius: 8, border: `1px solid ${T.bgHover}60`, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 56, height: 40, background: `linear-gradient(135deg, ${catColor}25, ${catColor}08)`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, border: `1px solid ${T.bgHover}40` }}>
                        {selectedType?.icon || ''}
                      </div>
                    )}
                    {/* Title + slug */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{getEntryTitle(e)}</span>
                        {e.is_featured && <span style={{ fontSize: 10, color: T.warning }}></span>}
                        {showAll && <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: 9, background: `${getTypeColor(e.content_type_id)}15`, color: getTypeColor(e.content_type_id) }}>{getTypeName(e.content_type_id)}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>/{e.slug}</div>
                    </div>
                    {/* Category */}
                    <div style={{ width: 100, textAlign: 'center' as const, flexShrink: 0 }}>
                      {e.category_id && catName !== '-' ? (
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: `${catColor}15`, color: catColor }}>
                          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: catColor, marginRight: 4 }} />
                          {catName}
                        </span>
                      ) : <span style={{ fontSize: 10, color: T.textDisabled }}>—</span>}
                    </div>
                    {/* Status */}
                    <div style={{ width: 100, textAlign: 'center' as const, flexShrink: 0 }}>
                      {renderStatusBadge(e.status)}
                    </div>
                    {/* Languages */}
                    <div style={{ width: 60, textAlign: 'center' as const, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: e.translations?.length > 1 ? T.success : T.textMuted }}>{e.translations?.length || 0}</span>
                    </div>
                    {/* Views */}
                    <div style={{ width: 60, textAlign: 'center' as const, flexShrink: 0, fontSize: 12, color: T.textSecondary }}>
                      {formatCount(e.view_count || 0)}
                    </div>
                    {/* SEO */}
                    <div style={{ width: 50, textAlign: 'center' as const, flexShrink: 0 }}>
                      {renderSeoCircle(seo)}
                    </div>
                    {/* Date */}
                    <div style={{ width: 80, textAlign: 'center' as const, flexShrink: 0, fontSize: 11, color: T.textMuted }}>
                      {e.published_at ? new Date(e.published_at).toLocaleDateString('pt-BR') : new Date(e.created_at).toLocaleDateString('pt-BR')}
                    </div>
                    {/* Actions */}
                    <div style={{ width: 40, flexShrink: 0 }} onClick={(ev: any) => ev.stopPropagation()}>
                      {renderActionMenu(e)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Floating bulk actions bar */}
          {selectedIds.length > 0 && (
            <div style={{
              position: 'fixed' as const, bottom: 24, left: '50%', transform: 'translateX(-50%)',
              background: T.bgOverlay, backdropFilter: 'blur(16px)',
              border: `1px solid ${T.borderHover}`, borderRadius: 14, padding: '10px 20px',
              display: 'flex', gap: 12, alignItems: 'center', zIndex: 100,
              boxShadow: `0 8px 32px ${T.bgOverlay}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{selectedIds.length} selecionado{selectedIds.length > 1 ? 's' : ''}</span>
              <div style={{ width: 1, height: 20, background: T.borderHover }} />
              <Button variant="ghost" size="sm" onClick={() => bulkAction('publish')}>Publicar</Button>
              <Button variant="ghost" size="sm" onClick={() => bulkAction('unpublish')}>Despublicar</Button>
              <Button variant="ghost" size="sm" onClick={() => bulkAction('delete')}>Excluir</Button>
              <Button variant="ghost" style={{ padding: '6px 10px', fontSize: 12, color: T.textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }} onClick={() => setSelectedIds([])}>✕</Button>
            </div>
          )}

          {/* Pagination */}
          {displayPages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
              <Button variant="ghost" onClick={() => setEntriesPage(Math.max(0, entriesPage - 1))} disabled={entriesPage === 0}>← Anterior</Button>
              <span style={{ padding: '8px 16px', fontSize: 13, color: T.textSecondary, background: T.bgModal, borderRadius: 8, border: `1px solid ${T.borderHover}` }}>
                {entriesPage + 1} / {displayPages}
              </span>
              <Button variant="ghost" onClick={() => setEntriesPage(Math.min(displayPages - 1, entriesPage + 1))} disabled={entriesPage >= displayPages - 1}>Proximo →</Button>
            </div>
          )}
        </div>
        );
      })()}

      {/* ═══ CATEGORIES TAB ═══ */}
      {selectedType && subTab === 'categories' && (() => {
        const catSearchVal = (filterSearch || '').toLowerCase();
        const publishedCats = categories.filter(c => c.status === 'published').length;
        const draftCats = categories.filter(c => c.status !== 'published').length;
        const totalEntries = categories.reduce((s, c) => s + (c.entry_count || 0), 0);
        const totalTranslations = categories.reduce((s, c) => s + (c.translation_count || 0), 0);
        const allCatLocales = Array.from(new Set(categories.flatMap(c => (c.translations || []).map(t => t.locale)))).sort();
        const catGroupByLang = !!filterCategory && filterCategory === '__group__';

        // Filter
        let filteredCats = categories;
        if (catSearchVal) {
          filteredCats = filteredCats.filter(c => {
            const name = c.translations?.[0]?.name || c.slug;
            return name.toLowerCase().includes(catSearchVal) || c.slug.toLowerCase().includes(catSearchVal);
          });
        }
        if (filterStatus === 'published') filteredCats = filteredCats.filter(c => c.status === 'published');
        else if (filterStatus === 'draft') filteredCats = filteredCats.filter(c => c.status !== 'published');

        // Build language groups
        const langGroups: Record<string, Category[]> = {};
        if (catGroupByLang) {
          for (const loc of allCatLocales) {
            const cats = filteredCats.filter(c => (c.translations || []).some(t => t.locale === loc));
            if (cats.length > 0) langGroups[loc] = cats;
          }
          const noTransCats = filteredCats.filter(c => !c.translations || c.translations.length === 0);
          if (noTransCats.length > 0) langGroups['sem-traducao'] = noTransCats;
        }

        const renderCatRow = (c: Category, idx: number, displayLocale?: string) => {
          const localeTranslation = displayLocale ? (c.translations || []).find(t => t.locale === displayLocale) : null;
          const name = localeTranslation?.name || c.translations?.[0]?.name || c.slug;
          const mainLocale = displayLocale || c.translations?.[0]?.locale || '-';
          const locales = (c.translations || []).map(t => t.locale);
          return (
            <tr key={c.id} style={{ borderBottom: '1px solid rgba(31,41,55,0.4)' }}>
              <td style={S.td}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button variant="ghost" size="xs" onClick={() => moveCatUp(categories.indexOf(c))} disabled={categories.indexOf(c) === 0}>&#9650;</Button>
                  <Button variant="ghost" size="xs" onClick={() => moveCatDown(categories.indexOf(c))} disabled={categories.indexOf(c) >= categories.length - 1}>&#9660;</Button>
                </div>
              </td>
              <td style={S.td}><span style={{ width: 20, height: 20, borderRadius: 4, background: c.color, display: 'inline-block' }} /></td>
              <td style={S.td}><code style={{ fontSize: 12, color: T.textSecondary }}>{c.icon || '-'}</code></td>
              <td style={S.td}>{name}</td>
              <td style={S.td}><code style={{ fontSize: 12, color: T.textSecondary }}>{c.slug}</code></td>
              <td style={S.td}>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${T.primary}20`, color: T.primary, border: `${T.primary}20` }}>{mainLocale}</span>
              </td>
              <td style={S.td}>
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' as const }}>
                  {locales.map(l => (
                    <span key={l} style={{ padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 500, background: l === mainLocale ? `${T.primary}18` : '#ffffff08', color: l === mainLocale ? T.primary : T.textSecondary }}>{l}</span>
                  ))}
                </div>
              </td>
              <td style={S.td}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: c.status === 'published' ? T.successBg : T.warningBg,
                  color: c.status === 'published' ? T.success : T.warning,
                  border: `1px solid ${c.status === 'published' ? T.success + '25' : `${T.warning}25`}`,
                }}>
                  {c.status === 'published' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success, animation: 'pulse 2s infinite' }} />}
                  {c.status === 'published' ? 'Publicado' : 'Rascunho'}
                </span>
              </td>
              <td style={S.td}>
                <span style={{ fontSize: 11, fontWeight: 600, background: (c.translation_count || 0) > 1 ? `${T.success}18` : `${T.textMuted}18`, color: (c.translation_count || 0) > 1 ? T.success : T.textMuted, padding: '2px 8px', borderRadius: 3 }}>
                  {c.translation_count || 0}
                </span>
              </td>
              <td style={S.td}>{c.entry_count || 0}</td>
              <td style={S.td}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                  <Button variant="ghost" onClick={() => openEditCat(c)}>Editar</Button>
                  {c.status === 'published' ? (
                    <Button variant="ghost" onClick={async () => { await api.post(`/admin/cms/categories/${c.id}/unpublish`); fetchCategories(); }}>Despublicar</Button>
                  ) : (
                    <Button variant="ghost" onClick={async () => { await api.post(`/admin/cms/categories/${c.id}/publish`); fetchCategories(); }}>Publicar</Button>
                  )}
                  <Button variant="ghost" onClick={() => translateCategory(c.id, 'all')} disabled={catTranslating}>
                    {catTranslatingIds[c.id] ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Spinner size="sm" inline />
                        Traduzindo...
                      </span>
                    ) : 'Traduzir'}
                  </Button>
                  <Button variant="ghost" onClick={() => deleteCat(c)}>Excluir</Button>
                </div>
              </td>
            </tr>
          );
        };

        const catTableHead = (
          <thead>
            <tr>
              <th style={S.th}>Ordem</th>
              <th style={S.th}>Cor</th>
              <th style={S.th}>Icone</th>
              <th style={S.th}>Nome</th>
              <th style={S.th}>Slug</th>
              <th style={S.th}>Idioma</th>
              <th style={S.th}>Traducoes</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Idiomas</th>
              <th style={S.th}>Entradas</th>
              <th style={S.th}>Acoes</th>
            </tr>
          </thead>
        );

        return (
        <div>
          {/* Stats cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total', value: categories.length, color: T.primary },
              { label: 'Publicadas', value: publishedCats, color: T.success },
              { label: 'Rascunhos', value: draftCats, color: T.warning },
              { label: 'Entradas', value: totalEntries, color: T.purple },
              { label: 'Traducoes', value: totalTranslations, color: '#06b6d4' },
            ].map(st => (
              <div key={st.label} style={{ background: T.bgOverlay, backdropFilter: 'blur(12px)', border: '1px solid rgba(31,41,55,0.6)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text, lineHeight: 1 }}>{st.value}</div>
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{st.label}</div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
            <Button variant="ghost" onClick={openNewCat}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Nova Categoria
            </Button>
            <Button variant="ghost" onClick={translateAllCategories} disabled={catTranslating}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04M18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12m-2.62 7l1.62-4.33L19.12 17h-3.24z"/></svg>
              {catTranslating ? 'Traduzindo...' : 'Traduzir Todas'}
            </Button>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' as const, marginBottom: 16 }}>
            <span style={{ position: 'absolute' as const, left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMuted }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </span>
            <input
              style={{ width: '100%', padding: '12px 14px 12px 42px', fontSize: 14, background: T.bgOverlay, backdropFilter: 'blur(8px)', border: `1px solid ${T.borderHover}`, borderRadius: 12, color: T.text, outline: 'none', boxSizing: 'border-box' as const }}
              value={filterSearch}
              onInput={(e: any) => setFilterSearch(e.target.value)}
              placeholder="Buscar categoria por nome, slug..."
            />
          </div>

          {/* Filter pills: Status + Group by language toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
            {[
              { value: '', label: 'Todas', color: T.textMuted },
              { value: 'published', label: 'Publicadas', color: T.success },
              { value: 'draft', label: 'Rascunhos', color: T.warning },
            ].map(st => (
              <Button variant="ghost" size="sm" onClick={() => setFilterStatus(filterStatus === st.value ? '' : st.value)}>{st.label}</Button>
            ))}

            <span style={{ width: 1, height: 20, background: T.borderHover }} />

            {/* Group by language toggle */}
            <Button variant="ghost" size="sm" onClick={() => setFilterCategory(catGroupByLang ? '' : '__group__')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04M18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12m-2.62 7l1.62-4.33L19.12 17h-3.24z"/></svg>
              Agrupar por idioma
            </Button>

            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: T.textMuted }}>{filteredCats.length} resultado{filteredCats.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Categories table */}
          {catGroupByLang ? (
            // Grouped by language — one table per locale
            Object.entries(langGroups).map(([lang, cats]) => (
              <div key={lang} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: lang === 'sem-traducao' ? `${T.warning}18` : `${T.primary}18`, color: lang === 'sem-traducao' ? T.warning : T.primary, border: `1px solid ${lang === 'sem-traducao' ? `${T.warning}18` : `${T.primary}18`}` }}>
                    {lang === 'sem-traducao' ? 'Sem traducao' : lang.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 12, color: T.textMuted }}>{cats.length} {cats.length === 1 ? 'categoria' : 'categorias'}</span>
                </div>
                <div style={{ ...S.card, overflowX: 'auto' as const }}>
                  <table style={S.table}>
                    {catTableHead}
                    <tbody>{cats.map((c, i) => renderCatRow(c, i, lang === 'sem-traducao' ? undefined : lang))}</tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            // Flat list — single table
            <div style={{ ...S.card, overflowX: 'auto' as const }}>
              <table style={S.table}>
                {catTableHead}
                <tbody>
                  {filteredCats.map((c, i) => renderCatRow(c, i))}
                  {filteredCats.length === 0 && (
                    <tr><td style={{ ...S.td, textAlign: 'center', color: T.textMuted }} colSpan={11}>Nenhuma categoria encontrada</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      {/* ═══ ANALYTICS TAB ═══ */}
      {selectedType && subTab === 'analytics' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[7, 30, 90, 0].map(d => (
              <Button variant="ghost" onClick={() => setAnalyticsDays(d || 9999)}>
                {d === 7 ? '7d' : d === 30 ? '30d' : d === 90 ? '90d' : 'Tudo'}
              </Button>
            ))}
          </div>
          <div style={S.row}>
            <div style={S.statsCard}>
              <p style={S.statsVal}>{analytics?.totals?.total_entries || 0}</p>
              <p style={S.statsLabel}>Entradas</p>
            </div>
            <div style={S.statsCard}>
              <p style={S.statsVal}>{(analytics?.totals?.views || 0).toLocaleString()}</p>
              <p style={S.statsLabel}>Visualizacoes</p>
            </div>
            <div style={S.statsCard}>
              <p style={S.statsVal}>{(analytics?.totals?.likes || 0).toLocaleString()}</p>
              <p style={S.statsLabel}>Curtidas</p>
            </div>
          </div>
          {analytics?.top_entries && analytics.top_entries.length > 0 && (
            <div style={S.card}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top 10 por views</h3>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Titulo</th>
                    <th style={S.th}>Views</th>
                    <th style={S.th}>Likes</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_entries.map((e: any) => (
                    <tr key={e.id}>
                      <td style={S.td}>{e.title || e.slug}</td>
                      <td style={S.td}>{e.view_count}</td>
                      <td style={S.td}>{e.like_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ MENUS TAB ═══ */}
      {selectedType && subTab === 'menus' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <Button variant="ghost" onClick={openNewMenu}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Novo Menu
            </Button>
          </div>
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Slug</th>
                <th style={S.th}>Location</th>
                <th style={S.th}>Items</th>
                <th style={S.th}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {menus.map(m => (
                <tr key={m.id}>
                  <td style={S.td}><code style={{ fontSize: 12, color: T.textSecondary }}>{m.slug}</code></td>
                  <td style={S.td}>{m.location}</td>
                  <td style={S.td}>{Array.isArray(m.items_json) ? m.items_json.length : 0} items</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="ghost" onClick={() => openEditMenu(m)}>Editar</Button>
                      <Button variant="ghost" onClick={() => deleteMenu(m)}>Excluir</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {menus.length === 0 && (
                <tr><td style={{ ...S.td, textAlign: 'center', color: T.textMuted }} colSpan={4}>Nenhum menu criado</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* ═══ SETTINGS TAB (Per-Type) ═══ */}
      {selectedType && subTab === 'settings' && (() => {
        const gs = (key: string) => {
          if (settingsEdited[key] !== undefined) return settingsEdited[key];
          const found = settings.find(s => s.setting_key === key);
          return found?.setting_value || '';
        };
        const setGs = (key: string, value: string) => {
          setSettingsEdited(prev => ({ ...prev, [key]: value }));
        };
        return (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
          <p style={{ fontSize: 12, color: T.textMuted }}>Configuracoes especificas para "{selectedType.label}". Salvas com content_type_id = {selectedType.id}.</p>
          {/* Identidade */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Identidade</h3>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>Nome do Site</label><input style={S.input} value={gs('site_name')} onInput={(e: any) => setGs('site_name', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={S.label}>Tagline</label><input style={S.input} value={gs('site_tagline')} onInput={(e: any) => setGs('site_tagline', e.target.value)} /></div>
            </div>
            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Logo URL</label>
                <input style={S.input} value={gs('site_logo_url')} onInput={(e: any) => setGs('site_logo_url', e.target.value)} placeholder="https://..." />
                {gs('site_logo_url') && <img src={gs('site_logo_url')} alt="Logo" style={{ height: 40, marginTop: 8, borderRadius: 4 }} onError={(e: any) => { e.target.style.display = 'none'; }} />}
              </div>
              <div style={{ flex: 1 }}><label style={S.label}>Favicon URL</label><input style={S.input} value={gs('site_favicon_url')} onInput={(e: any) => setGs('site_favicon_url', e.target.value)} /></div>
            </div>
          </div>
          {/* Contato */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Contato</h3>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>Email</label><input style={S.input} value={gs('contact_email')} onInput={(e: any) => setGs('contact_email', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={S.label}>Telefone</label><input style={S.input} value={gs('contact_phone')} onInput={(e: any) => setGs('contact_phone', e.target.value)} /></div>
            </div>
            <div style={{ marginTop: 8 }}><label style={S.label}>Endereco</label><input style={S.input} value={gs('contact_address')} onInput={(e: any) => setGs('contact_address', e.target.value)} /></div>
          </div>
          {/* Redes Sociais */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Redes Sociais</h3>
            {['twitter', 'facebook', 'instagram', 'linkedin', 'youtube', 'github'].map(net => (
              <div key={net} style={{ marginBottom: 8 }}>
                <label style={S.label}>{net.charAt(0).toUpperCase() + net.slice(1)}</label>
                <input style={S.input} value={gs(`social_${net}`)} onInput={(e: any) => setGs(`social_${net}`, e.target.value)} placeholder={`https://${net}.com/...`} />
              </div>
            ))}
          </div>
          {/* Analytics */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Analytics</h3>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>Google Analytics ID</label><input style={S.input} value={gs('analytics_ga_id')} onInput={(e: any) => setGs('analytics_ga_id', e.target.value)} placeholder="G-XXXXXXXXXX" /></div>
              <div style={{ flex: 1 }}><label style={S.label}>Google Tag Manager ID</label><input style={S.input} value={gs('analytics_gtm_id')} onInput={(e: any) => setGs('analytics_gtm_id', e.target.value)} placeholder="GTM-XXXXXXX" /></div>
            </div>
          </div>
          {/* Scripts */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Scripts Customizados</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Scripts no HEAD</label>
              <Textarea style={{ ...S.textarea, minHeight: 100 }} value={gs('scripts_head')} onInput={(e: any) => setGs('scripts_head', e.target.value)} placeholder="<script>...</script>" />
            </div>
            <div>
              <label style={S.label}>Scripts no BODY</label>
              <Textarea style={{ ...S.textarea, minHeight: 100 }} value={gs('scripts_body')} onInput={(e: any) => setGs('scripts_body', e.target.value)} placeholder="<script>...</script>" />
            </div>
          </div>
          {/* Localizacao */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Localizacao</h3>
            <div style={S.row}>
              <div style={{ flex: 1 }}><label style={S.label}>Idioma Padrao</label><input style={S.input} value={gs('default_locale')} onInput={(e: any) => setGs('default_locale', e.target.value)} placeholder="pt-BR" /></div>
              <div style={{ flex: 1 }}><label style={S.label}>Timezone</label><input style={S.input} value={gs('timezone')} onInput={(e: any) => setGs('timezone', e.target.value)} placeholder="America/Sao_Paulo" /></div>
            </div>
          </div>
          {/* Footer */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Rodape</h3>
            <div style={{ marginBottom: 8 }}><label style={S.label}>Texto do Rodape</label><input style={S.input} value={gs('footer_text')} onInput={(e: any) => setGs('footer_text', e.target.value)} /></div>
            <div><label style={S.label}>Copyright</label><input style={S.input} value={gs('copyright_text')} onInput={(e: any) => setGs('copyright_text', e.target.value)} /></div>
          </div>
          {/* Prompt de Texto Especifico */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Prompt de Texto para este Tipo</h3>
            <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Instrucoes especificas para geracao e traducao de texto deste tipo. Combinado com o prompt global de texto.</p>
            <Textarea
              style={{ ...S.textarea, minHeight: 180, fontFamily: 'monospace', fontSize: 12 }}
              value={gs('prompt_type_text')}
              onInput={(e: any) => setGs('prompt_type_text', e.target.value)}
              placeholder="Instrucoes especificas de texto para este tipo de conteudo..."
            />
          </div>
          {/* Prompt de Conteudo Especifico */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Prompt de Conteudo para este Tipo</h3>
            <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Instrucoes especificas para geracao automatica de conteudo deste tipo. Combinado com o prompt global de conteudo.</p>
            <Textarea
              style={{ ...S.textarea, minHeight: 180, fontFamily: 'monospace', fontSize: 12 }}
              value={gs('prompt_type_content')}
              onInput={(e: any) => setGs('prompt_type_content', e.target.value)}
              placeholder="Instrucoes especificas de conteudo para este tipo..."
            />
          </div>
          {/* Prompt de Imagem Especifico */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Prompt de Imagem para este Tipo</h3>
            <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>Instrucoes especificas para geracao de imagens deste tipo. Combinado com o prompt global de imagem.</p>
            <Textarea
              style={{ ...S.textarea, minHeight: 180, fontFamily: 'monospace', fontSize: 12 }}
              value={gs('prompt_type_image')}
              onInput={(e: any) => setGs('prompt_type_image', e.target.value)}
              placeholder="Instrucoes especificas de imagem para este tipo de conteudo..."
            />
          </div>
          {/* Quantidade padrao de imagens internas */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Imagens Internas</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Quantidade padrao de imagens internas</label>
              <input
                type="number"
                min={0}
                max={10}
                style={{ ...S.input, width: 100 }}
                value={gs('default_inline_images') || '3'}
                onInput={(e: any) => setGs('default_inline_images', e.target.value)}
              />
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Numero de imagens geradas automaticamente dentro do conteudo (0 = nenhuma)</div>
            </div>
          </div>
          {/* Custom key-value */}
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Configuracoes Adicionais</h3>
            <table style={S.table}>
              <tbody>
                {settings.filter(s => !['site_name','site_tagline','site_logo_url','site_favicon_url','contact_email','contact_phone','contact_address','social_twitter','social_facebook','social_instagram','social_linkedin','social_youtube','social_github','analytics_ga_id','analytics_gtm_id','scripts_head','scripts_body','default_locale','timezone','footer_text','copyright_text','prompt_type','prompt_type_text','prompt_type_image','prompt_content_generation','prompt_type_content'].includes(s.setting_key)).map(s => (
                  <tr key={s.id}>
                    <td style={S.td}><code style={{ fontSize: 12 }}>{s.setting_key}</code></td>
                    <td style={S.td}>
                      <input style={{ ...S.input, width: 400 }} value={settingsEdited[s.setting_key] !== undefined ? settingsEdited[s.setting_key] : s.setting_value} onInput={(e: any) => setSettingsEdited(prev => ({ ...prev, [s.setting_key]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={S.td}><input style={{ ...S.input, width: 200 }} value={newSettingKey} onInput={(e: any) => setNewSettingKey(e.target.value)} placeholder="nova_chave" /></td>
                  <td style={S.td}><input style={{ ...S.input, width: 400 }} value={newSettingVal} onInput={(e: any) => setNewSettingVal(e.target.value)} placeholder="valor" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Save */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={saveSettings} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar configuracoes'}
            </Button>
          </div>
        </div>
        );
      })()}

      {/* ═══ AUDIT LOG TAB ═══ */}
      {selectedType && subTab === 'audit' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[7, 30, 90].map(d => (
              <Button variant="ghost" onClick={() => setAuditDays(d)}>
                {d}d
              </Button>
            ))}
          </div>
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Usuario</th>
                  <th style={S.th}>Acao</th>
                  <th style={S.th}>Tipo</th>
                  <th style={S.th}>Entity ID</th>
                  <th style={S.th}>Data</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(l => (
                  <tr key={l.id}>
                    <td style={S.td}>{l.user_name || l.user_id}</td>
                    <td style={S.td}><span style={S.badge(T.primary)}>{l.action}</span></td>
                    <td style={S.td}>{l.entity_type}</td>
                    <td style={S.td}><code style={{ fontSize: 11, color: T.textMuted }}>{(l.entity_id || '').substring(0, 8)}</code></td>
                    <td style={S.td}><span style={{ fontSize: 12, color: T.textSecondary }}>{new Date(l.created_at).toLocaleString('pt-BR')}</span></td>
                  </tr>
                ))}
                {auditLogs.length === 0 && (
                  <tr><td style={{ ...S.td, textAlign: 'center', color: T.textMuted }} colSpan={5}>Nenhum log encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ USERS TAB ═══ */}
      {selectedType && subTab === 'users' && (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nome</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Role</th>
                <th style={S.th}>Criado em</th>
                <th style={S.th}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {cmsUsers.map((u: any) => (
                <tr key={u.id}>
                  <td style={S.td}>{u.display_name || u.customer_name || '-'}</td>
                  <td style={S.td}>{u.email || u.customer_email || '-'}</td>
                  <td style={S.td}>
                    <select style={{ ...S.select, width: 120 }} value={u.role} onChange={(e: any) => updateCmsUserRole(u.id, e.target.value)}>
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="autor">Autor</option>
                      <option value="revisor">Revisor</option>
                    </select>
                  </td>
                  <td style={S.td}><span style={{ fontSize: 12, color: T.textSecondary }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}</span></td>
                  <td style={S.td}>
                    <Button variant="ghost" onClick={() => deleteCmsUser(u.id)}>Remover</Button>
                  </td>
                </tr>
              ))}
              {cmsUsers.length === 0 && (
                <tr><td style={{ ...S.td, textAlign: 'center', color: T.textMuted }} colSpan={5}>Nenhum usuario CMS cadastrado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ MEDIA TAB ═══ */}
      {selectedType && subTab === 'media' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <input style={{ ...S.input, width: 250 }} value={mediaSearch} onInput={(e: any) => setMediaSearch(e.target.value)} placeholder="Buscar por nome..." />
            <input style={{ ...S.input, width: 150 }} value={mediaFolder} onInput={(e: any) => setMediaFolder(e.target.value)} placeholder="Pasta" />
            <Button variant="ghost" onClick={() => { setMediaSearch(''); setMediaFolder(''); }}>Limpar</Button>
          </div>
          {/* Media Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {mediaItems.map((m: any) => {
              const isImage = (m.mime_type || '').startsWith('image/');
              return (
                <div key={m.id} style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
                  {isImage ? (
                    <div style={{ height: 120, background: T.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={m.storage_url || m.thumbnail_url} alt={m.alt_text || m.filename} style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' as const }} onError={(e: any) => { e.target.style.display = 'none'; }} />
                    </div>
                  ) : (
                    <div style={{ height: 120, background: T.bgSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 24 }}>
                      DOC
                    </div>
                  )}
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={m.original_filename}>{m.original_filename || m.filename}</div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{m.mime_type} {m.size_bytes > 0 ? `| ${(m.size_bytes / 1024).toFixed(0)}KB` : ''}</div>
                    <input
                      style={{ ...S.input, fontSize: 10, marginTop: 4, padding: '3px 6px' }}
                      value={m.alt_text || ''}
                      placeholder="Alt text"
                      onBlur={(e: any) => updateMediaAlt(m.id, e.target.value)}
                      onChange={() => {}}
                    />
                    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                      <Button variant="ghost" size="xs" onClick={() => { navigator.clipboard.writeText(m.storage_url || ''); }}>Copiar URL</Button>
                      <Button variant="ghost" size="xs" onClick={() => deleteMedia(m.id)}>Excluir</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {mediaItems.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhuma midia encontrada. Adicione via URL ou upload.</div>
          )}
        </div>
      )}

      {/* === GLOBAL CONFIG TAB === */}
      {subTab === 'global-config' && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: T.text }}>Configuracoes Globais</h2>
          <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>Configure provedor, modelos e autenticacao para todas as operacoes de IA do CMS.</p>
          {globalSettingsLoading ? (
            <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>

              {/* STEP 1: Conexao API */}
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: T.primaryBg, color: T.primary, fontSize: 12, fontWeight: 700 }}>1</span>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: 0 }}>Conexao API</h3>
                </div>
                <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Informe a URL e o token do Elon Router para conectar aos modelos de IA.</p>

                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
                  <div>
                    <label style={S.label}>URL DA API</label>
                    <input
                      style={S.input}
                      value={globalSettings.llm_provider_url || ''}
                      onInput={(e: any) => updateGlobalSetting('llm_provider_url', e.target.value)}
                      placeholder="/api/v1/llm"
                    />
                  </div>
                  <div>
                    <label style={S.label}>TOKEN DE AUTENTICACAO</label>
                    <input
                      type="password"
                      style={S.input}
                      value={globalSettings.llm_api_token || globalSettings.llm_token || ''}
                      onInput={(e: any) => updateGlobalSetting('llm_api_token', e.target.value)}
                      placeholder="ert_live_..."
                    />
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Token criado em Admin &gt; Elon Router &gt; Tokens.</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="ghost" onClick={testConnection} disabled={testingConnection}>
                      {testingConnection ? 'Testando...' : 'Testar Conexao e Buscar Modelos'}
                    </Button>
                  </div>
                  {connectionTestResult && (
                    <div style={{
                      padding: '6px 10px', borderRadius: 4, fontSize: 12,
                      background: connectionTestResult.ok ? '#064e3b' : T.dangerBg,
                      color: connectionTestResult.ok ? T.success : T.danger,
                      border: `1px solid ${connectionTestResult.ok ? T.successBg : '#991b1b'}`,
                    }}>
                      {connectionTestResult.ok ? '[OK] ' : '[ERRO] '}{connectionTestResult.message}
                    </div>
                  )}
                  {/* Show fetched models list */}
                  {opModelsCache['elon']?.length > 0 && (
                    <div style={{ fontSize: 11, color: T.textMuted, padding: '6px 10px', borderRadius: 6, background: T.bgCard, border: `1px solid ${T.border}` }}>
                      Modelos disponiveis: {opModelsCache['elon'].map((m, i) => (
                        <span key={m} style={{ color: T.text }}>{i > 0 ? ', ' : ''}{m}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* STEP 2: Modelos por operacao */}
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: opModelsCache['elon']?.length ? T.primaryBg : T.border, color: opModelsCache['elon']?.length ? T.primary : T.textDisabled, fontSize: 12, fontWeight: 700 }}>2</span>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: 0 }}>Modelos por Operacao</h3>
                </div>
                {!opModelsCache['elon']?.length ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
                    Clique "Testar Conexao e Buscar Modelos" acima para carregar os modelos disponiveis.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
                    <p style={{ fontSize: 12, color: T.textMuted, margin: 0 }}>Selecione o modelo para cada operacao de IA do CMS.</p>
                    {[
                      { key: 'translation', label: 'Traducao', desc: 'Traduzir conteudo entre idiomas', modelKey: 'llm_translation_model' },
                      { key: 'content', label: 'Geracao de Conteudo', desc: 'Criar artigos e textos completos', modelKey: 'llm_content_model' },
                      { key: 'idea', label: 'Geracao de Ideias', desc: 'Sugestoes de titulos e topicos', modelKey: 'llm_idea_model' },
                      { key: 'image', label: 'Geracao de Imagens', desc: 'Criar imagens via IA', modelKey: 'llm_image_model' },
                      { key: 'summary', label: 'Resumo Automatico', desc: 'Resumir conteudo existente', modelKey: 'llm_summary_model' },
                      { key: 'restyle', label: 'Reestilizar', desc: 'Reescrever em outro tom/estilo', modelKey: 'llm_restyle_model' },
                    ].map(op => {
                      const currentModel = globalSettings[op.modelKey] || '';
                      const apiModels = opModelsCache['elon'] || [];
                      return (
                        <div key={op.key} style={{
                          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' as const,
                          padding: '10px 12px', borderRadius: 8,
                          background: T.bgSubtle,
                          border: `1px solid ${T.border}`,
                        }}>
                          <div style={{ flex: '0 0 180px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 2 }}>{op.label}</div>
                            <div style={{ fontSize: 11, color: T.textMuted }}>{op.desc}</div>
                          </div>
                          <div style={{ flex: '1 1 250px' }}>
                            <label style={{ ...S.label, fontSize: 10 }}>MODELO</label>
                            <select style={{ ...S.select, fontSize: 12, padding: '6px 8px' }} value={currentModel} onChange={(e: any) => updateGlobalSetting(op.modelKey, e.target.value)}>
                              <option value="">-- Selecionar modelo --</option>
                              {apiModels.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            {currentModel && !apiModels.includes(currentModel) && (
                              <div style={{ fontSize: 10, color: T.warning, marginTop: 2 }}>Modelo atual "{currentModel}" nao esta na API. Selecione um modelo valido.</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Prompts Section */}
              <div style={S.card}>
                <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: T.text }}>Prompts Globais</h3>
                <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>Instrucoes que definem tom, estilo e diretrizes para todas as operacoes de IA.</p>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ ...S.label, marginBottom: 6 }}>Prompt de Texto (traducao, resumo, reestilizacao)</label>
                  <Textarea
                    style={{ ...S.textarea, minHeight: 160, fontFamily: 'monospace', fontSize: 12 }}
                    value={globalSettings.prompt_global_text || ''}
                    onInput={(e: any) => updateGlobalSetting('prompt_global_text', e.target.value)}
                    placeholder="Instrucoes globais para geracao de texto..."
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ ...S.label, marginBottom: 6 }}>Prompt de Geracao de Conteudo</label>
                  <Textarea
                    style={{ ...S.textarea, minHeight: 160, fontFamily: 'monospace', fontSize: 12 }}
                    value={globalSettings.prompt_content_generation || ''}
                    onInput={(e: any) => updateGlobalSetting('prompt_content_generation', e.target.value)}
                    placeholder="Instrucoes para geracao automatica de conteudo..."
                  />
                </div>

                <div>
                  <label style={{ ...S.label, marginBottom: 6 }}>Prompt de Imagem</label>
                  <Textarea
                    style={{ ...S.textarea, minHeight: 160, fontFamily: 'monospace', fontSize: 12 }}
                    value={globalSettings.prompt_global_image || ''}
                    onInput={(e: any) => updateGlobalSetting('prompt_global_image', e.target.value)}
                    placeholder="Instrucoes globais para geracao de imagens..."
                  />
                </div>
              </div>

              {/* Save */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={saveGlobalSettings} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar Configuracoes'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {subTab === 'sitemap' && (() => {
        const typeMap: Record<string, any> = {};
        for (const t of sitemapData.types) typeMap[t.id] = t;

        // Build URL list
        const allUrls: { tipo: string; recurso: string; slug: string; locale: string; url: string; status: string; updated: string; typeId: string }[] = [];

        // Home pages per type per locale
        const localesPerType: Record<string, Set<string>> = {};
        for (const e of sitemapData.entries) {
          if (!localesPerType[e.content_type_id]) localesPerType[e.content_type_id] = new Set();
          localesPerType[e.content_type_id].add(e.locale as string);
        }
        for (const cat of sitemapData.categories) {
          if (!localesPerType[cat.content_type_id]) localesPerType[cat.content_type_id] = new Set();
          localesPerType[cat.content_type_id].add(cat.locale as string);
        }

        for (const [typeId, locales] of Object.entries(localesPerType)) {
          const t = typeMap[typeId];
          if (!t?.subdomain) continue;
          for (const loc of locales) {
            allUrls.push({ tipo: t.label, recurso: 'Home', slug: '/', locale: loc, url: `https://${t.subdomain}.example.com/${loc}`, status: 'published', updated: '', typeId });
          }
        }

        // Entries
        for (const e of sitemapData.entries) {
          const t = typeMap[e.content_type_id];
          if (!t?.subdomain) continue;
          allUrls.push({ tipo: t.label, recurso: 'Entry', slug: e.slug as string, locale: e.locale as string, url: `https://${t.subdomain}.example.com/${e.locale}/${e.slug}`, status: e.status as string, updated: (e.updated_at || e.published_at || '') as string, typeId: e.content_type_id as string });
        }

        // Categories
        for (const cat of sitemapData.categories) {
          const t = typeMap[cat.content_type_id];
          if (!t?.subdomain) continue;
          allUrls.push({ tipo: t.label, recurso: 'Categoria', slug: cat.slug as string, locale: cat.locale as string, url: `https://${t.subdomain}.example.com/${cat.locale}/${cat.slug}`, status: 'published', updated: '', typeId: cat.content_type_id as string });
        }

        // Filter by selected content type (if one is selected)
        const filteredUrls = selectedType
          ? allUrls.filter(u => u.typeId === selectedType.id)
          : allUrls;

        // Sort by type, recurso, slug, locale
        filteredUrls.sort((a, b) => a.tipo.localeCompare(b.tipo) || a.recurso.localeCompare(b.recurso) || a.slug.localeCompare(b.slug) || a.locale.localeCompare(b.locale));

        const filteredEntries = selectedType
          ? sitemapData.entries.filter((e: any) => e.content_type_id === selectedType.id)
          : sitemapData.entries;
        const filteredCats = selectedType
          ? sitemapData.categories.filter((c: any) => c.content_type_id === selectedType.id)
          : sitemapData.categories;
        const entryCount = filteredEntries.length;
        const catCount = filteredCats.length;
        const uniqueEntries = new Set(filteredEntries.map((e: any) => e.id)).size;
        const uniqueCats = new Set(filteredCats.map((c: any) => c.id)).size;
        const entryLocales = new Set(filteredEntries.map((e: any) => e.locale)).size;
        const catLocales = new Set(filteredCats.map((c: any) => c.locale)).size;
        const homeCount = filteredUrls.filter(u => u.recurso === 'Home').length;

        const copyAllUrls = () => {
          const text = filteredUrls.map(u => u.url).join('\n');
          navigator.clipboard.writeText(text);
        };

        const exportSitemapXml = () => {
          let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
          for (const u of filteredUrls) {
            xml += '  <url>\n';
            xml += `    <loc>${u.url}</loc>\n`;
            if (u.updated) xml += `    <lastmod>${u.updated.split('T')[0]}</lastmod>\n`;
            xml += `    <changefreq>${u.recurso === 'Home' ? 'daily' : 'weekly'}</changefreq>\n`;
            xml += `    <priority>${u.recurso === 'Home' ? '1.0' : u.recurso === 'Entry' ? '0.8' : '0.6'}</priority>\n`;
            xml += '  </url>\n';
          }
          xml += '</urlset>';
          const blob = new Blob([xml], { type: 'application/xml' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'sitemap.xml';
          a.click();
        };

        const showTipo = !sitemapFilter;

        return (
          <div>
            {/* Stats */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' as const }}>
              <div style={{ ...S.card, padding: '12px 16px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Total URLs</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{filteredUrls.length}</div>
              </div>
              <div style={{ ...S.card, padding: '12px 16px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Entradas</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{uniqueEntries} <span style={{ fontSize: 12, color: T.textMuted }}>({entryLocales} locales)</span></div>
              </div>
              <div style={{ ...S.card, padding: '12px 16px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Categorias</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{uniqueCats} <span style={{ fontSize: 12, color: T.textMuted }}>({catLocales} locales)</span></div>
              </div>
              <div style={{ ...S.card, padding: '12px 16px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Home Pages</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{homeCount}</div>
              </div>
            </div>

            {/* Filters + Actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' as const }}>
              <select style={S.select} value={sitemapFilter} onChange={(e: any) => setSitemapFilter(e.target.value)}>
                <option value="">Todos os tipos</option>
                {sitemapData.types.map((t: any) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <Button variant="ghost" onClick={copyAllUrls}>Copiar Todos URLs</Button>
              <Button variant="ghost" onClick={exportSitemapXml}>Exportar sitemap.xml</Button>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' as const }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.borderHover}`, color: T.textMuted, fontSize: 11, textTransform: 'uppercase' as const }}>
                    {showTipo && <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Tipo</th>}
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Recurso</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Slug</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Idioma</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>URL Completa</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Status</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' as const }}>Ultima Atualizacao</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUrls.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #2F2F2F' }}>
                      {showTipo && <td style={{ padding: '8px 10px', color: T.textSecondary }}>{u.tipo}</td>}
                      <td style={{ padding: '8px 10px', color: T.textSecondary }}>{u.recurso}</td>
                      <td style={{ padding: '8px 10px', color: T.text, fontFamily: 'monospace', fontSize: 12 }}>{u.slug}</td>
                      <td style={{ padding: '8px 10px', color: T.textSecondary }}>{u.locale}</td>
                      <td style={{ padding: '8px 10px' }}><a href={u.url} target="_blank" rel="noopener noreferrer" style={{ color: T.primary, textDecoration: 'none', fontSize: 12 }}>{u.url}</a></td>
                      <td style={{ padding: '8px 10px' }}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, background: u.status === 'published' ? '#064e3b' : T.textDisabled, color: u.status === 'published' ? T.success : T.textSecondary }}>{u.status}</span></td>
                      <td style={{ padding: '8px 10px', color: T.textMuted, fontSize: 11 }}>{u.updated ? new Date(u.updated).toLocaleDateString('pt-BR') : '-'}</td>
                    </tr>
                  ))}
                  {filteredUrls.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center' as const, color: T.textMuted }}>Nenhuma URL encontrada. Publique conteudo primeiro.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ═══ CUSTOS TAB ═══ */}
      {subTab === 'costs' && (() => {
        const s = costsSummary || {};
        const fmtCost = (v: number) => v != null ? `$${v.toFixed(4)}` : '$0.0000';
        const costColor = (v: number) => v < 0.01 ? T.success : v < 0.10 ? T.warningHover : T.danger;
        const periods = [{ v: '7', l: '7 dias' }, { v: '30', l: '30 dias' }, { v: '90', l: '90 dias' }, { v: '3650', l: 'Tudo' }];
        return (
          <div>
            {/* Stats cards */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
              {[
                { label: 'Custo Total', value: fmtCost(s.total_cost_usd || 0), color: T.primary },
                { label: 'Traducoes', value: `${fmtCost(s.translation_cost || 0)} (${s.translation_count || 0})`, color: T.purple },
                { label: 'Imagens', value: `${fmtCost(s.image_cost || 0)} (${s.image_count || 0})`, color: T.success },
                { label: 'Resumos', value: `${fmtCost(s.summary_cost || 0)} (${s.summary_count || 0})`, color: T.warning },
                { label: 'Falhas', value: `${s.failed_count || 0}`, color: (s.failed_count || 0) > 0 ? T.danger : T.textMuted },
              ].map((card) => (
                <div key={card.label} style={{ background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 6, padding: '12px 16px', minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>{card.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: card.color }}>{card.value}</div>
                </div>
              ))}
            </div>
            {/* Period filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {periods.map(p => (
                <Button variant="ghost" size="xs" onClick={() => setCostsPeriod(p.v)}>{p.l}</Button>
              ))}
            </div>
            {/* Table */}
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Data</th><th style={S.th}>Operacao</th><th style={S.th}>Entrada</th>
                <th style={S.th}>Modelo</th><th style={S.th}>Locale</th><th style={S.th}>Tokens (In/Out)</th>
                <th style={S.th}>Custo (USD)</th><th style={S.th}>Duracao</th><th style={S.th}>Status</th>
              </tr></thead>
              <tbody>
                {costsList.map((c: any) => (
                  <tr key={c.id} style={S.tr}>
                    <td style={S.td}>{c.created_at ? new Date(c.created_at + 'Z').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                    <td style={S.td}>{c.operation === 'translation' ? 'Traducao' : c.operation === 'image_generation' ? 'Imagem' : c.operation === 'summary' ? 'Resumo' : c.operation}</td>
                    <td style={S.td}>{c.entry_slug || '-'}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>{c.model}</td>
                    <td style={S.td}>{c.locale || '-'}</td>
                    <td style={S.td}>{c.input_tokens || 0} / {c.output_tokens || 0}</td>
                    <td style={{ ...S.td, color: costColor(c.cost_usd || 0), fontWeight: 600 }}>{fmtCost(c.cost_usd || 0)}</td>
                    <td style={S.td}>{c.duration_ms ? `${(c.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                    <td style={S.td}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.status === 'success' ? T.successBg : T.dangerBg, color: c.status === 'success' ? T.success : T.danger }}>{c.status === 'success' ? 'OK' : 'Falha'}</span></td>
                  </tr>
                ))}
                {costsList.length === 0 && <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center' as const, color: T.textMuted }}>Nenhum custo registrado</td></tr>}
                {costsList.length > 0 && (
                  <tr style={{ background: T.bg }}>
                    <td colSpan={6} style={{ ...S.td, fontWeight: 700 }}>Total ({costsList.length} operacoes)</td>
                    <td style={{ ...S.td, fontWeight: 700, color: T.primary }}>{fmtCost(costsList.reduce((sum: number, c: any) => sum + (c.cost_usd || 0), 0))}</td>
                    <td colSpan={2} style={S.td}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ═══ LANGUAGES TAB ═══ */}
      {subTab === 'languages' && (() => {
        const filtered = allLocales.filter(l =>
          !localeSearch || l.code.toLowerCase().includes(localeSearch.toLowerCase()) ||
          l.name.toLowerCase().includes(localeSearch.toLowerCase()) ||
          (l.native_name || '').toLowerCase().includes(localeSearch.toLowerCase())
        );
        const activeCount = allLocales.filter(l => l.is_active).length;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{activeCount} de {allLocales.length} idiomas ativos</span>
              <input style={{ ...S.input, maxWidth: 250 }} placeholder="Buscar idioma..." value={localeSearch} onInput={(e: any) => setLocaleSearch(e.target.value)} />
              <Button variant="ghost" onClick={() => bulkUpdateLocales(allLocales.map(l => l.code), 1)}>Ativar Todos</Button>
              <Button variant="ghost" onClick={() => bulkUpdateLocales(allLocales.map(l => l.code), 0)}>Desativar Todos</Button>
              <Button variant="ghost" onClick={() => {
                const allCodes = allLocales.map(l => l.code);
                // First deactivate all, then activate top 20
                bulkUpdateLocales(allCodes, 0).then(() => bulkUpdateLocales(TOP_20_LOCALES, 1));
              }}>Apenas Principais (20)</Button>
            </div>
            {localesLoading ? <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div> : (
              <div style={{ maxHeight: '65vh', overflowY: 'auto' as const, border: `1px solid ${T.borderHover}`, borderRadius: 6 }}>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Codigo</th><th style={S.th}>Nome</th><th style={S.th}>Nome Nativo</th><th style={S.th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(l => (
                      <tr key={l.code}>
                        <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{l.code}</td>
                        <td style={S.td}>{l.name}</td>
                        <td style={S.td}>{l.native_name || '-'}</td>
                        <td style={S.td}>
                          <Button variant="ghost" onClick={() => toggleLocale(l.code)}>{l.is_active ? 'Ativo' : 'Inativo'}</Button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center' as const, color: T.textMuted }}>Nenhum idioma encontrado</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ MONITORING TAB ═══ */}
      {subTab === 'monitoring' && (() => {
        const stats = monitoringStats || { by_operation: [], totals: {} };
        const t = stats.totals || {};
        const fmtCost = (v: number) => `$${(v || 0).toFixed(4)}`;
        const fmtTokens = (v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${v || 0}`;
        const fmtDuration = (ms: number) => ms >= 60000 ? `${(ms / 60000).toFixed(1)}min` : `${((ms || 0) / 1000).toFixed(1)}s`;
        const periods = [{ v: 'today', l: 'Hoje' }, { v: '7d', l: '7 dias' }, { v: '30d', l: '30 dias' }, { v: 'all', l: 'Tudo' }];
        const opLabels: Record<string, { label: string; icon: string; color: string }> = {
          idea_generation: { label: 'Ideias', icon: '*', color: T.warning },
          content_generation: { label: 'Conteudo', icon: '', color: T.primary },
          translation: { label: 'Traducoes', icon: '', color: T.purple },
          image_generation: { label: 'Imagens', icon: '*', color: T.success },
          restyle: { label: 'Restyle', icon: '*', color: T.danger },
          summary: { label: 'Resumo', icon: '*', color: '#06b6d4' },
        };
        return (
          <div>
            {/* Period filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {periods.map(p => (
                <Button variant="ghost" size="sm" onClick={() => setMonitoringPeriod(p.v)}>{p.l}</Button>
              ))}
            </div>
            {monitoringLoading ? <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div> : (<>
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
                {[
                  { label: 'Total Operacoes', value: `${t.total_ops || 0}`, color: T.primary },
                  { label: 'Total Tokens', value: fmtTokens(t.total_tokens || 0), color: T.purple },
                  { label: 'Custo Total', value: fmtCost(t.total_cost_usd || 0), color: T.success },
                  { label: 'Tempo Medio', value: fmtDuration(t.avg_duration_ms || 0), color: T.warning },
                  { label: 'Sucesso / Erro', value: `${t.success_count || 0} / ${t.error_count || 0}`, color: (t.error_count || 0) > 0 ? T.danger : T.success },
                ].map(card => (
                  <div key={card.label} style={{ flex: 1, minWidth: 140, background: T.bgModal, border: `1px solid ${T.borderHover}`, borderRadius: 6, padding: '14px 16px', textAlign: 'center' as const }}>
                    <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 6, textTransform: 'uppercase' as const }}>{card.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
                  </div>
                ))}
              </div>

              {/* By operation breakdown */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const }}>
                {(stats.by_operation || []).map((op: any) => {
                  const meta = opLabels[op.operation] || { label: op.operation, icon: '', color: T.textMuted };
                  const successRate = op.total_ops > 0 ? Math.round((op.success_count / op.total_ops) * 100) : 0;
                  return (
                    <div key={op.operation} style={{ flex: 1, minWidth: 180, background: T.bgModal, border: `1px solid ${meta.color}30`, borderRadius: 6, padding: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: meta.color }}>{meta.icon} {meta.label}</div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>Operacoes: <span style={{ color: T.text, fontWeight: 600 }}>{op.total_ops}</span></div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>Tokens: <span style={{ color: T.text, fontWeight: 600 }}>{fmtTokens((op.total_input_tokens || 0) + (op.total_output_tokens || 0))}</span></div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>Custo: <span style={{ color: T.text, fontWeight: 600 }}>{fmtCost(op.total_cost_usd || 0)}</span></div>
                      <div style={{ fontSize: 12, color: T.textSecondary }}>Sucesso: <span style={{ color: successRate >= 90 ? T.success : successRate >= 70 ? T.warning : T.danger, fontWeight: 600 }}>{successRate}%</span></div>
                    </div>
                  );
                })}
                {(stats.by_operation || []).length === 0 && <div style={{ color: T.textMuted, padding: 20 }}>Nenhuma operacao registrada neste periodo.</div>}
              </div>

              {/* Recent operations table */}
              <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 10 }}>Ultimas Operacoes</h3>
              <div style={{ maxHeight: '40vh', overflowY: 'auto' as const, border: `1px solid ${T.borderHover}`, borderRadius: 6 }}>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Data</th><th style={S.th}>Operacao</th><th style={S.th}>Entrada</th>
                    <th style={S.th}>Modelo</th><th style={S.th}>Locale</th><th style={S.th}>Tokens</th>
                    <th style={S.th}>Custo</th><th style={S.th}>Duracao</th><th style={S.th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {monitoringRecent.map((op: any) => {
                      const meta = opLabels[op.operation] || { label: op.operation, icon: '', color: T.textMuted };
                      const durationSlow = (op.duration_ms || 0) > 30000;
                      return (
                        <tr key={op.id}>
                          <td style={S.td}>{op.created_at ? new Date(op.created_at + 'Z').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td style={{ ...S.td, color: meta.color }}>{meta.icon} {meta.label}</td>
                          <td style={{ ...S.td, fontSize: 11 }}>{op.entry_slug || '-'}</td>
                          <td style={{ ...S.td, fontSize: 11 }}>{op.model || '-'}</td>
                          <td style={S.td}>{op.locale || '-'}</td>
                          <td style={S.td}>{(op.input_tokens || 0) + (op.output_tokens || 0)}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{fmtCost(op.cost_usd || 0)}</td>
                          <td style={{ ...S.td, color: durationSlow ? T.warning : T.textSecondary }}>{op.duration_ms ? `${(op.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                          <td style={S.td}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: op.status === 'success' ? T.successBg : T.dangerBg, color: op.status === 'success' ? T.success : T.danger }}>{op.status === 'success' ? 'OK' : 'Erro'}</span></td>
                        </tr>
                      );
                    })}
                    {monitoringRecent.length === 0 && <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center' as const, color: T.textMuted }}>Nenhuma operacao recente</td></tr>}
                  </tbody>
                </table>
              </div>
            </>)}
          </div>
        );
      })()}

      {/* ═══ USER MODAL ═══ */}
      {showUserModal && (
        <div style={S.modalOverlay} onClick={() => setShowUserModal(false)}>
          <div style={{ ...S.modal, maxWidth: 400 }} onClick={(e: any) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Novo Usuario CMS</h2>
            <div style={{ marginBottom: 12 }}><label style={S.label}>Customer ID</label><input style={S.input} value={newUserCustomerId} onInput={(e: any) => setNewUserCustomerId(e.target.value)} placeholder="UUID do customer" /></div>
            <div style={{ marginBottom: 12 }}><label style={S.label}>Email</label><input style={S.input} value={newUserEmail} onInput={(e: any) => setNewUserEmail(e.target.value)} placeholder="email@exemplo.com" /></div>
            <div style={{ marginBottom: 12 }}><label style={S.label}>Nome</label><input style={S.input} value={newUserName} onInput={(e: any) => setNewUserName(e.target.value)} placeholder="Nome de exibicao" /></div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Role</label>
              <select style={S.select} value={newUserRole} onChange={(e: any) => setNewUserRole(e.target.value)}>
                <option value="admin">Admin — acesso total</option>
                <option value="editor">Editor — criar, editar, publicar</option>
                <option value="autor">Autor — criar e editar proprio conteudo</option>
                <option value="revisor">Revisor — ver e aprovar, sem criar</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShowUserModal(false)}>Cancelar</Button>
              <Button variant="ghost" onClick={saveCmsUser} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MEDIA URL MODAL ═══ */}
      {showMediaUrlModal && (
        <div style={S.modalOverlay} onClick={() => setShowMediaUrlModal(false)}>
          <div style={{ ...S.modal, maxWidth: 450 }} onClick={(e: any) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Adicionar Midia por URL</h2>
            <div style={{ marginBottom: 12 }}><label style={S.label}>URL</label><input style={S.input} value={newMediaUrl} onInput={(e: any) => setNewMediaUrl(e.target.value)} placeholder="https://..." /></div>
            <div style={{ marginBottom: 12 }}><label style={S.label}>Alt Text</label><input style={S.input} value={newMediaAlt} onInput={(e: any) => setNewMediaAlt(e.target.value)} placeholder="Descricao da imagem" /></div>
            <div style={{ marginBottom: 16 }}><label style={S.label}>Pasta</label><input style={S.input} value={newMediaFolder} onInput={(e: any) => setNewMediaFolder(e.target.value)} placeholder="geral" /></div>
            {newMediaUrl && (
              <div style={{ marginBottom: 16, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.borderHover}` }}>
                <img src={newMediaUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' as const }} onError={(e: any) => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShowMediaUrlModal(false)}>Cancelar</Button>
              <Button variant="ghost" onClick={addMediaUrl} disabled={saving}>{saving ? 'Salvando...' : 'Adicionar'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ENTRY MODAL ═══ */}
      {showEntryModal && selectedType && (
        <div style={{ position: 'fixed' as const, inset: 0, background: T.bg, zIndex: 1000, overflowY: 'auto' as const, display: 'flex', flexDirection: 'column' as const }}>
          {/* Top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: `1px solid ${T.borderHover}`, background: T.bgModal, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: T.text }}>
                {editEntry ? 'Editar' : 'Novo'} {selectedType.label}
              </h2>
              {editEntry && <span style={{ fontSize: 12, color: T.textSecondary }}>/{editEntry.slug}</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button variant="ghost" onClick={() => setShowEntryModal(false)}>Cancelar</Button>
              <Button variant="ghost" onClick={() => saveEntry('draft')} disabled={saving}>
                {saving ? '...' : 'Salvar Rascunho'}
              </Button>
              <Button variant="ghost" onClick={() => saveEntry('published')} disabled={saving}>
                {saving ? '...' : 'Publicar'}
              </Button>
              <Button variant="ghost" size="xs" onClick={() => setShowEntryModal(false)}>X</Button>
            </div>
          </div>

          {/* Content area — 2 columns */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Left: main content */}
            <div style={{ flex: 1, overflowY: 'auto' as const, padding: '20px 24px' }}>

              {/* Locale tabs */}
              <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.borderHover}`, marginBottom: 16 }}>
                {['pt-BR', 'en'].map(loc => (
                  <Button variant="ghost" onClick={() => setFLocale(loc)}>
                    {loc}
                  </Button>
                ))}
              </div>

              {/* Title */}
              <div style={{ marginBottom: 12 }}>
                <label style={S.label}>Titulo ({fLocale})</label>
                <input
                  style={{ ...S.input, fontSize: 18, fontWeight: 600, padding: '12px 14px' }}
                  value={fTranslations[fLocale]?.title || ''}
                  onInput={(e: any) => {
                    updateTransField(fLocale, 'title', e.target.value);
                    if (!editEntry && fLocale === 'pt-BR') setFSlug(slugify(e.target.value));
                  }}
                  placeholder="Titulo"
                />
              </div>

              {/* Excerpt */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Resumo ({fLocale})</label>
                <input
                  style={S.input}
                  value={fTranslations[fLocale]?.excerpt || ''}
                  onInput={(e: any) => updateTransField(fLocale, 'excerpt', e.target.value)}
                  placeholder="Breve resumo do conteudo..."
                />
              </div>

              {/* Body — Markdown editor with toolbar */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Conteudo ({fLocale})</label>
                {/* Toolbar */}
                <div style={{ display: 'flex', gap: 2, padding: '6px 8px', background: T.bgModal, border: `1px solid ${T.borderHover}`, borderBottom: 'none', borderRadius: '4px 4px 0 0', flexWrap: 'wrap' as const, alignItems: 'center' }}>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('**', '**', 'negrito')}>B</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('*', '*', 'italico')}>I</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('# ', '', 'Titulo')}>H1</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('## ', '', 'Subtitulo')}>H2</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('[', '](url)', 'texto')}>Link</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('![', '](url)', 'alt')}>Img</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('`', '`', 'codigo')}>{'<>'}</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('> ', '', 'citacao')}>Quote</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('- ', '', 'item')}>List</Button>
                  <Button variant="ghost" size="xs" onClick={() => insertMarkdown('\n---\n', '', '')}>---</Button>
                  {editEntry && (
                    <Button variant="ghost" size="xs" onClick={async () => {
                        if (!editEntry) return;
                        setRestyling(true);
                        try {
                          const r = await api.post<any>(`/admin/cms/entries/${editEntry.id}/restyle`, { locale: fLocale });
                          const data = r.data || r;
                          if (data.body) {
                            updateTransField(fLocale, 'body', data.body);
                            alert(`Reestilizado com sucesso! Modelo: ${data.model}, Custo: $${(data.cost_usd || 0).toFixed(4)}`);
                          }
                        } catch (err: any) {
                          alert('Erro ao reestilizar: ' + (err.message || 'Erro desconhecido'));
                        }
                        setRestyling(false);
                      }} disabled={restyling || !fTranslations[fLocale]?.body}>
                      {restyling ? 'Reestilizando...' : 'Reestilizar com IA'}
                    </Button>
                  )}
                  {editEntry && (!fTranslations[fLocale]?.body || editEntry.status === 'idea') && (
                    <Button variant="ghost" size="xs" onClick={async () => {
                        if (!editEntry) return;
                        setGeneratingContent(true);
                        try {
                          const r = await api.post<any>(`/admin/cms/entries/${editEntry.id}/generate-content`);
                          const data = r.data || r;
                          const updated = await api.get<any>(`/admin/cms/entries/${editEntry.id}`);
                          const ud = updated.data || updated;
                          if (ud.translations) {
                            const t = ud.translations.find((tt: any) => tt.locale === fLocale);
                            if (t) {
                              updateTransField(fLocale, 'body', t.body || '');
                              updateTransField(fLocale, 'excerpt', t.excerpt || '');
                              updateTransField(fLocale, 'meta_description', t.meta_description || '');
                            }
                          }
                          alert(`Conteudo gerado! ${data.word_count} palavras, custo: $${(data.cost_usd || 0).toFixed(4)}`);
                          fetchEntries();
                        } catch (err: any) {
                          alert('Erro: ' + (err.message || 'Erro desconhecido'));
                        }
                        setGeneratingContent(false);
                      }} disabled={generatingContent}>
                      {generatingContent ? 'Gerando conteudo...' : 'Gerar Conteudo com IA'}
                    </Button>
                  )}
                  {editEntry && (
                    <Button variant="ghost" size="xs" onClick={async () => {
                        if (!editEntry) return;
                        const countStr = prompt('Quantas imagens gerar dentro do conteudo?', '3');
                        if (!countStr) return;
                        const count = parseInt(countStr);
                        if (isNaN(count) || count < 1 || count > 10) { alert('Numero invalido (1-10)'); return; }
                        setGeneratingInlineImages(true);
                        try {
                          const r = await api.post<any>(`/admin/cms/entries/${editEntry.id}/generate-inline-images`, { count });
                          const data = r.data || r;
                          if (data.generated > 0) {
                            const updated = await api.get<any>(`/admin/cms/entries/${editEntry.id}`);
                            const ud = updated.data || updated;
                            if (ud.translations) {
                              const t = ud.translations.find((tt: any) => tt.locale === fLocale);
                              if (t?.body) updateTransField(fLocale, 'body', t.body);
                            }
                            alert(`${data.generated} imagens geradas com sucesso!${data.failed > 0 ? ` (${data.failed} falharam)` : ''}`);
                          } else {
                            alert('Nenhuma imagem gerada. Verifique a chave OpenAI.');
                          }
                        } catch (err: any) {
                          alert('Erro: ' + (err.message || 'Erro desconhecido'));
                        }
                        setGeneratingInlineImages(false);
                      }} disabled={generatingInlineImages || !fTranslations[fLocale]?.body}>
                      {generatingInlineImages ? 'Gerando imagens...' : 'Gerar Imagens no Conteudo'}
                    </Button>
                  )}
                  <div style={{ flex: 1 }} />
                  {/* Editor mode tabs */}
                  <div style={{ display: 'flex', gap: 0, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.borderHover}` }}>
                    {(['write', 'preview', 'split'] as const).map(mode => (
                      <Button variant="ghost" size="xs" onClick={() => setEditorMode(mode)}>
                        {mode === 'write' ? 'Escrever' : mode === 'preview' ? 'Preview' : 'Lado a lado'}
                      </Button>
                    ))}
                  </div>
                </div>
                {/* Editor area */}
                <div style={{ display: 'flex', border: `1px solid ${T.borderHover}`, borderRadius: '0 0 4px 4px', minHeight: 'calc(100vh - 440px)' }}>
                  {(editorMode === 'write' || editorMode === 'split') && (
                    <Textarea
                      data-md-editor="true"
                      style={{ ...S.textarea, minHeight: 'calc(100vh - 440px)', flex: 1, border: 'none', borderRadius: editorMode === 'split' ? '0 0 0 4px' : '0 0 4px 4px', ...(editorMode === 'split' ? { borderRight: `1px solid ${T.borderHover}` } : {}) }}
                      value={fTranslations[fLocale]?.body || ''}
                      onInput={(e: any) => updateTransField(fLocale, 'body', e.target.value)}
                      placeholder="Escreva o conteudo em Markdown..."
                    />
                  )}
                  {(editorMode === 'preview' || editorMode === 'split') && (
                    <div
                      style={{ flex: 1, padding: '12px 16px', overflowY: 'auto' as const, background: T.bg, color: T.text, fontSize: 14, lineHeight: 1.6, borderRadius: editorMode === 'split' ? '0 0 4px 0' : '0 0 4px 4px', minHeight: 'calc(100vh - 440px)' }}
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(fTranslations[fLocale]?.body || '') }}
                    />
                  )}
                </div>
              </div>

              {/* SEO / Meta fields */}
              <div style={{ ...S.card, marginBottom: 16 }}>
                <label style={{ ...S.label, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>SEO ({fLocale})</label>
                <div style={S.row}>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Meta Title</label>
                    <input style={S.input} value={fTranslations[fLocale]?.meta_title || ''} onInput={(e: any) => updateTransField(fLocale, 'meta_title', e.target.value)} placeholder="Titulo para mecanismos de busca" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Meta Description</label>
                    <input style={S.input} value={fTranslations[fLocale]?.meta_description || ''} onInput={(e: any) => updateTransField(fLocale, 'meta_description', e.target.value)} placeholder="Descricao para mecanismos de busca" />
                  </div>
                </div>
              </div>

              {/* Code Blocks section */}
              <div style={{ ...S.card, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ ...S.label, fontSize: 12, fontWeight: 600, margin: 0 }}>Blocos de Codigo</label>
                  <Button variant="ghost" size="xs" onClick={() => {
                    const nextId = fCodeBlocks.length > 0 ? Math.max(...fCodeBlocks.map(b => b.id)) + 1 : 1;
                    setFCodeBlocks([...fCodeBlocks, { id: nextId, language: 'bash', label: '', code: '' }]);
                  }}>Adicionar Bloco</Button>
                </div>
                {fCodeBlocks.length === 0 && <div style={{ color: T.textMuted, fontSize: 12 }}>Nenhum bloco de codigo. Adicione blocos e use {'{{CODE_BLOCK_N}}'} no conteudo.</div>}
                {fCodeBlocks.map((block, idx) => (
                  <div key={block.id} style={{ border: `1px solid ${T.borderHover}`, borderRadius: 8, padding: 12, marginBottom: 10, background: T.bg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: T.warning, background: T.bg, padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(`{{CODE_BLOCK_${block.id}}}`); }}>{`{{CODE_BLOCK_${block.id}}}`}</span>
                      <Button variant="ghost" onClick={() => setFCodeBlocks(fCodeBlocks.filter((_, i) => i !== idx))}>Remover</Button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...S.label, fontSize: 11 }}>Linguagem</label>
                        <select style={{ ...S.select, fontSize: 12 }} value={block.language} onChange={(e: any) => { const nb = [...fCodeBlocks]; nb[idx] = { ...block, language: e.target.value }; setFCodeBlocks(nb); }}>
                          {['bash','javascript','typescript','python','json','html','css','sql','yaml','toml','go','rust','java','csharp','php','ruby','swift','kotlin','text'].map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 2 }}>
                        <label style={{ ...S.label, fontSize: 11 }}>Label</label>
                        <input style={{ ...S.input, fontSize: 12 }} value={block.label} onInput={(e: any) => { const nb = [...fCodeBlocks]; nb[idx] = { ...block, label: e.target.value }; setFCodeBlocks(nb); }} placeholder="Ex: cURL Example" />
                      </div>
                    </div>
                    <Textarea style={{ ...S.textarea, minHeight: 80, fontFamily: 'monospace', fontSize: 12 }} value={block.code} onInput={(e: any) => { const nb = [...fCodeBlocks]; nb[idx] = { ...block, code: e.target.value }; setFCodeBlocks(nb); }} placeholder="Cole o codigo aqui..." />
                  </div>
                ))}
              </div>

            </div>{/* end left column */}

            {/* Right sidebar — metadata */}
            <div style={{ width: 300, borderLeft: `1px solid ${T.borderHover}`, overflowY: 'auto' as const, padding: '20px 16px', background: T.bgModal, flexShrink: 0 }}>

              {/* Slug */}
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Slug / URL</label>
                <input style={S.input} value={fSlug} onInput={(e: any) => setFSlug(e.target.value)} placeholder="slug-do-conteudo" />
              </div>

              {/* Category */}
              {selectedType.has_categories && (
                <div style={{ marginBottom: 14 }}>
                  <label style={S.label}>Categoria</label>
                  <select style={S.select} value={fCatId} onChange={(e: any) => setFCatId(e.target.value)}>
                    <option value="">Selecione</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.translations?.[0]?.name || c.slug}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Author */}
              {selectedType.has_author && (
                <div style={{ marginBottom: 14 }}>
                  <label style={S.label}>Autor</label>
                  <input style={S.input} value={fAuthor} onInput={(e: any) => setFAuthor(e.target.value)} placeholder="Nome do autor" />
                </div>
              )}

              {/* Tags */}
              {selectedType.has_tags && (
                <div style={{ marginBottom: 14 }}>
                  <label style={S.label}>Tags</label>
                  <input style={S.input} value={fTags} onInput={(e: any) => setFTags(e.target.value)} placeholder="tag1, tag2, tag3" />
                </div>
              )}

              {/* Cover Image */}
              {selectedType.has_cover_image && (
                <div style={{ marginBottom: 14 }}>
                  <label style={S.label}>Imagem de Capa</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...S.input, flex: 1 }} value={fCover} onInput={(e: any) => setFCover(e.target.value)} placeholder="https://..." />
                    {editEntry && (
                      <Button variant="ghost" size="xs" onClick={async () => {
                          if (!editEntry) return;
                          setGeneratingImage(true);
                          try {
                            const r = await api.post<any>(`/admin/cms/entries/${editEntry.id}/generate-image`, { type: 'cover' });
                            const url = r.data?.url || r.url;
                            if (url) setFCover(url);
                          } catch (err: any) {
                            alert('Erro ao gerar imagem: ' + (err?.message || 'Erro desconhecido'));
                          }
                          setGeneratingImage(false);
                        }} disabled={generatingImage}>
                        {generatingImage ? 'Gerando...' : (fCover ? 'Gerar Novamente' : 'Gerar Imagem')}
                      </Button>
                    )}
                  </div>
                  {fCover && (
                    <div style={{ marginTop: 8, borderRadius: 4, overflow: 'hidden', border: `1px solid ${T.borderHover}` }}>
                      <img src={fCover} alt="Preview" style={{ width: '100%', height: 120, objectFit: 'cover' as const }} onError={(e: any) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
              )}

              {/* Schedule */}
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Agendar Publicacao</label>
                <input style={S.input} type="datetime-local" value={fScheduled} onInput={(e: any) => setFScheduled(e.target.value)} />
              </div>

              {/* Featured */}
              {selectedType.has_featured && (
                <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ ...S.label, margin: 0 }}>Destaque</label>
                  <Button variant="ghost" onClick={() => setFFeatured(!fFeatured)}>
                    <span style={S.toggleDot(fFeatured)} />
                  </Button>
                </div>
              )}

              {/* Custom fields */}
              {customFields.length > 0 && (
                <div style={{ marginBottom: 14, borderTop: `1px solid ${T.borderHover}`, paddingTop: 14 }}>
                  <label style={{ ...S.label, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Campos do tipo</label>
                  {customFields.map(cf => (
                    <div key={cf.key} style={{ marginBottom: 10 }}>
                      <label style={S.label}>{cf.label || cf.key}</label>
                      {cf.type === 'textarea' ? (
                        <Textarea
                          style={{ ...S.textarea, minHeight: 80 }}
                          value={fTranslations[fLocale]?.custom_fields?.[cf.key] || ''}
                          onInput={(e: any) => updateCustomField(fLocale, cf.key, e.target.value)}
                        />
                      ) : cf.type === 'boolean' ? (
                        <Button variant="ghost" onClick={() => updateCustomField(fLocale, cf.key, !fTranslations[fLocale]?.custom_fields?.[cf.key])}>
                          <span style={S.toggleDot(!!fTranslations[fLocale]?.custom_fields?.[cf.key])} />
                        </Button>
                      ) : cf.type === 'select' ? (
                        <select
                          style={S.select}
                          value={fTranslations[fLocale]?.custom_fields?.[cf.key] || ''}
                          onChange={(e: any) => updateCustomField(fLocale, cf.key, e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {(cf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : cf.type === 'number' ? (
                        <input style={S.input} type="number" value={fTranslations[fLocale]?.custom_fields?.[cf.key] || ''} onInput={(e: any) => updateCustomField(fLocale, cf.key, e.target.value)} />
                      ) : (
                        <input style={S.input} value={fTranslations[fLocale]?.custom_fields?.[cf.key] || ''} onInput={(e: any) => updateCustomField(fLocale, cf.key, e.target.value)} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Auto-translate */}
              {editEntry && (
                <div style={{ borderTop: `1px solid ${T.borderHover}`, paddingTop: 14 }}>
                  <label style={{ ...S.label, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Traducao via IA</label>
                  <select style={{ ...S.select, marginBottom: 8 }} value={translateModel} onChange={(e: any) => setTranslateModel(e.target.value)}>
                    {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <Button variant="ghost" fullWidth onClick={() => translateEntry(editEntry.id, 'all')} disabled={translating}>
                    {translating ? 'Traduzindo...' : 'Traduzir todos os idiomas'}
                  </Button>
                  <Button variant="ghost" fullWidth onClick={() => translateEntry(editEntry.id, [fLocale])} disabled={translating || fLocale === 'pt-BR'}>
                    {translating ? '...' : `Traduzir ${fLocale}`}
                  </Button>
                  {translateResult && (
                    <div style={{ marginTop: 8, fontSize: 11, color: translateResult.failed > 0 ? T.warningHover : T.success }}>
                      {translateResult.translated}/{translateResult.total} traduzidos
                      {translateResult.failed > 0 && ` | ${translateResult.failed} falhou`}
                    </div>
                  )}
                </div>
              )}

              {/* Entry Costs */}
              {editEntry && entryCosts && entryCosts.costs.length > 0 && (
                <div style={{ borderTop: `1px solid ${T.borderHover}`, paddingTop: 14 }}>
                  <label style={{ ...S.label, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Custos LLM</label>
                  <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6 }}>
                    Total: <span style={{ fontWeight: 700, color: T.primary }}>${(entryCosts.total_usd || 0).toFixed(4)}</span>
                  </div>
                  {(() => {
                    const byOp: Record<string, number> = {};
                    for (const cc of entryCosts.costs) byOp[cc.operation] = (byOp[cc.operation] || 0) + (cc.cost_usd || 0);
                    const parts: string[] = [];
                    if (byOp['translation']) parts.push(`Traducao $${byOp['translation'].toFixed(4)}`);
                    if (byOp['image_generation']) parts.push(`Imagens $${byOp['image_generation'].toFixed(4)}`);
                    return parts.length > 0 ? <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 6 }}>{parts.join(' | ')}</div> : null;
                  })()}
                  <Button variant="ghost" size="xs" onClick={() => setShowEntryCostDetails(!showEntryCostDetails)}>
                    {showEntryCostDetails ? 'Ocultar detalhes' : 'Ver detalhes'}
                  </Button>
                  {showEntryCostDetails && (
                    <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' as const }}>
                      {entryCosts.costs.map((cc: any) => (
                        <div key={cc.id} style={{ fontSize: 10, color: T.textSecondary, padding: '4px 0', borderBottom: `1px solid ${T.borderHover}` }}>
                          <span style={{ color: cc.status === 'success' ? T.success : T.danger }}>{cc.status === 'success' ? 'OK' : 'ERR'}</span>
                          {' '}{cc.operation === 'translation' ? 'Trad' : cc.operation === 'image_generation' ? 'Img' : cc.operation}
                          {cc.locale && ` (${cc.locale})`}
                          {' '}${(cc.cost_usd || 0).toFixed(4)}
                          {' '}{cc.input_tokens || 0}/{cc.output_tokens || 0}t
                          {cc.duration_ms ? ` ${(cc.duration_ms / 1000).toFixed(1)}s` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>{/* end right sidebar */}
          </div>{/* end 2-column layout */}
        </div>
      )}

      {/* ═══ CATEGORY MODAL ═══ */}
      {showCatModal && selectedType && (
        <div style={S.modalOverlay} onClick={() => setShowCatModal(false)}>
          <div style={{ ...S.modal, maxWidth: 500 }} onClick={(e: any) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
              {editCat ? 'Editar Categoria' : 'Nova Categoria'}
            </h2>

            {/* Locale tabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.borderHover}`, marginBottom: 16, flexWrap: 'wrap' as const }}>
              {Object.keys(cfTranslations).map(loc => (
                <Button variant="ghost" onClick={() => setCfLocale(loc)}>
                  {loc}
                </Button>
              ))}
              <Button variant="ghost" size="xs" onClick={() => {
                  const code = prompt('Codigo do idioma (ex: es, fr, de, ja):');
                  if (code && !cfTranslations[code]) {
                    setCfTranslations(prev => ({ ...prev, [code]: { name: '', description: '' } }));
                    setCfLocale(code);
                  }
                }}>+ Idioma</Button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Nome ({cfLocale})</label>
              <input
                style={S.input}
                value={cfTranslations[cfLocale]?.name || ''}
                onInput={(e: any) => {
                  updateCatTransField(cfLocale, 'name', e.target.value);
                  if (!editCat && cfLocale === 'pt-BR') setCfSlug(slugify(e.target.value));
                }}
                placeholder="Nome da categoria"
              />
            </div>

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Slug</label>
                <input style={S.input} value={cfSlug} onInput={(e: any) => setCfSlug(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Sort Order</label>
                <input style={S.input} type="number" value={cfSortOrder} onInput={(e: any) => setCfSortOrder(parseInt(e.target.value) || 0)} />
              </div>
            </div>

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Cor (hex)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={cfColor} onInput={(e: any) => setCfColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }} />
                  <input style={S.input} value={cfColor} onInput={(e: any) => setCfColor(e.target.value)} placeholder={T.primary} />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Icone (nome)</label>
                <input style={S.input} value={cfIcon} onInput={(e: any) => setCfIcon(e.target.value)} placeholder="brain, code, etc" />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Descricao ({cfLocale})</label>
              <Textarea
                style={{ ...S.textarea, minHeight: 100 }}
                value={cfTranslations[cfLocale]?.description || ''}
                onInput={(e: any) => updateCatTransField(cfLocale, 'description', e.target.value)}
                placeholder="Descricao da categoria"
              />
            </div>

            {/* Auto-translate */}
            {editCat && (
              <div style={{ marginBottom: 16, padding: 12, background: T.bg, borderRadius: 6, border: `1px solid ${T.border}` }}>
                <label style={{ ...S.label, marginBottom: 8 }}>Traducao automatica</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={() => translateCategory(editCat.id, 'all')} disabled={catTranslating}>
                    {catTranslating ? 'Traduzindo...' : 'Traduzir todos os idiomas'}
                  </Button>
                  <Button variant="ghost" onClick={() => translateCategory(editCat.id, [cfLocale])} disabled={catTranslating}>
                    {catTranslating ? '...' : `Traduzir ${cfLocale}`}
                  </Button>
                </div>
                {catTranslateResult && (
                  <div style={{ marginTop: 8, fontSize: 11, color: catTranslateResult.failed > 0 ? T.warningHover : T.success }}>
                    {catTranslateResult.translated}/{catTranslateResult.total} traduzidos
                    {catTranslateResult.failed > 0 && ` | ${catTranslateResult.failed} falhou`}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShowCatModal(false)}>Cancelar</Button>
              <Button variant="ghost" onClick={saveCat} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SCHEDULING TAB ═══ */}
      {subTab === 'scheduling' && (() => {
        const dayLabels = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];
        const freqLabels: Record<string, string> = { hourly: 'hora(s)', daily: 'dia(s)', weekly: 'semana(s)', custom: 'personalizado' };

        const formatCountdown = (isoDate: string) => {
          const diff = new Date(isoDate).getTime() - Date.now();
          if (diff <= 0) return 'agora';
          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          return h > 0 ? `em ${h}h ${m}min` : `em ${m}min`;
        };

        const saveRule = async (data: any) => {
          try {
            if (schedEditRule?.id) {
              await api.put(`/admin/cms/schedule/rules/${schedEditRule.id}`, data);
            } else {
              await api.post('/admin/cms/schedule/rules', data);
            }
            setSchedShowRuleModal(false);
            setSchedEditRule(null);
            fetchScheduling();
          } catch { /* ignore */ }
        };

        const toggleRule = async (id: string) => {
          await api.post(`/admin/cms/schedule/rules/${id}/toggle`, {});
          fetchScheduling();
        };

        const deleteRule = async (id: string) => {
          if (!confirm('Excluir esta regra e todos os itens na fila?')) return;
          await api.delete(`/admin/cms/schedule/rules/${id}`);
          fetchScheduling();
        };

        const cancelQueueItem = async (id: string) => {
          await api.post(`/admin/cms/schedule/queue/${id}/cancel`, {});
          fetchScheduling();
        };

        const removeQueueItem = async (id: string) => {
          await api.delete(`/admin/cms/schedule/queue/${id}`);
          fetchScheduling();
        };

        const processNow = async () => {
          setSchedProcessing(true);
          try {
            await api.post('/admin/cms/schedule/process', {});
            fetchScheduling();
          } catch { /* ignore */ }
          setSchedProcessing(false);
        };

        // Calendar helpers
        const calYear = parseInt(schedCalMonth.split('-')[0]);
        const calMon = parseInt(schedCalMonth.split('-')[1]) - 1;
        const daysInMonth = new Date(calYear, calMon + 1, 0).getDate();
        const firstDayOfWeek = (new Date(calYear, calMon, 1).getDay() + 6) % 7; // 0=Mon

        const cardStyle = {
          background: T.bgOverlay, border: `1px solid ${T.borderHover}`, borderRadius: 14,
          padding: 20, backdropFilter: 'blur(12px)', marginBottom: 16,
        } as any;

        const pillStyle = (active: boolean) => ({
          display: 'inline-block', padding: '3px 10px', fontSize: 11, fontWeight: 500, borderRadius: 8,
          background: active ? T.primaryBg : T.bgCard,
          color: active ? T.primary : T.textMuted, border: `1px solid ${active ? T.primaryBg : T.borderHover}`,
          marginRight: 4,
        } as any);

        const toggleStyle = (on: boolean) => ({
          width: 40, height: 22, borderRadius: 11, background: on ? T.success : T.textDisabled,
          position: 'relative' as const, cursor: 'pointer', transition: 'background 0.2s', border: 'none',
          display: 'inline-block',
        });

        const toggleKnob = (on: boolean) => ({
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          position: 'absolute' as const, top: 3, left: on ? 21 : 3, transition: 'left 0.2s',
        });

        const statusBadge = (s: string) => {
          const colors: Record<string, string> = { pending: T.warning, publishing: T.primary, published: T.success, failed: T.danger, cancelled: T.textMuted };
          return { display: 'inline-block', padding: '2px 8px', fontSize: 11, borderRadius: 6, background: `${colors[s] || T.textMuted}20`, color: colors[s] || T.textMuted, fontWeight: 500 };
        };

        return (
          <div>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Agendamento de Publicacoes</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={processNow} disabled={schedProcessing}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  {schedProcessing ? 'Processando...' : 'Processar Fila'}
                </Button>
                <Button variant="ghost" onClick={() => { setSchedEditRule(null); setSchedShowRuleModal(true); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Nova Regra
                </Button>
              </div>
            </div>

            {schedLoading && <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>}

            {!schedLoading && (
              <>
                {/* ── a) Rules ── */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 12, textTransform: 'uppercase' as const, letterSpacing: 1 }}>Regras de Agendamento</div>
                  {schedRules.length === 0 && (
                    <div style={{ ...cardStyle, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 }}>Nenhuma regra criada. Crie uma regra para agendar publicacoes automaticamente.</div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
                    {schedRules.map((rule: any) => {
                      const hours = JSON.parse(rule.preferred_hours || '[]');
                      const days = JSON.parse(rule.preferred_days || '[]');
                      const cats = JSON.parse(rule.category_ids || '[]');
                      return (
                        <div key={rule.id} style={{ ...cardStyle, opacity: rule.is_active ? 1 : 0.5 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{rule.name || 'Sem nome'}</div>
                              <div style={{ fontSize: 12, color: T.textSecondary }}>
                                A cada {rule.frequency_value} {freqLabels[rule.frequency_type] || rule.frequency_type}
                                {hours.length > 0 && `, as ${hours.map((h: number) => `${String(h).padStart(2, '0')}:00`).join(', ')}`}
                              </div>
                            </div>
                            <Button variant="ghost" onClick={() => toggleRule(rule.id)}>
                              <div style={toggleKnob(!!rule.is_active)} />
                            </Button>
                          </div>
                          {/* Day pills */}
                          <div style={{ marginBottom: 8 }}>
                            {[1,2,3,4,5,6,7].map(d => (
                              <span key={d} style={pillStyle(days.includes(d))}>{dayLabels[d]}</span>
                            ))}
                          </div>
                          {/* Info row */}
                          <div style={{ display: 'flex', gap: 16, fontSize: 11, color: T.textMuted, marginBottom: 12 }}>
                            <span>Max/dia: {rule.max_per_day}</span>
                            <span>Tipo: {rule.content_type_id}</span>
                            {cats.length > 0 && <span>{cats.length} categoria(s)</span>}
                            {rule.auto_translate ? <span style={{ color: T.primary }}>Auto-traducao</span> : null}
                            {rule.auto_cover ? <span style={{ color: T.purple }}>Auto-capa</span> : null}
                          </div>
                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Button variant="ghost" size="xs" onClick={() => { setSchedEditRule(rule); setSchedShowRuleModal(true); }}>Editar</Button>
                            <Button variant="ghost" size="xs" onClick={() => deleteRule(rule.id)}>Excluir</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── b) Queue ── */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' as const, letterSpacing: 1 }}>Fila de Publicacao ({schedQueue.length})</div>
                  </div>
                  {schedQueue.length === 0 && (
                    <div style={{ ...cardStyle, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 }}>Fila vazia. Processe as regras ou adicione itens manualmente.</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                    {schedQueue.map((qi: any) => (
                      <div key={qi.id} style={{ ...cardStyle, padding: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 4 }}>{qi.entry_title || qi.entry_id}</div>
                          <div style={{ display: 'flex', gap: 10, fontSize: 11, color: T.textMuted, alignItems: 'center' }}>
                            {qi.category_name && <span style={{ ...pillStyle(true), marginRight: 0 }}>{qi.category_name}</span>}
                            <span>{new Date(qi.scheduled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            <span style={{ color: T.warning }}>{formatCountdown(qi.scheduled_at)}</span>
                            <span style={statusBadge(qi.status)}>{qi.status}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button variant="ghost" size="xs" onClick={() => cancelQueueItem(qi.id)}>Cancelar</Button>
                          <Button variant="ghost" size="xs" onClick={() => removeQueueItem(qi.id)}>Remover</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── c) Calendar ── */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, cursor: 'pointer' }}
                    onClick={() => setSchedShowCalendar(!schedShowCalendar)}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' as const, letterSpacing: 1 }}>
                      Calendario
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" style={{ marginLeft: 8, transform: schedShowCalendar ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Button variant="ghost" size="xs" onClick={(e: any) => { e.stopPropagation(); const d = new Date(calYear, calMon - 1, 1); setSchedCalMonth(d.toISOString().slice(0, 7)); }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                      </Button>
                      <span style={{ fontSize: 13, color: T.text, minWidth: 120, textAlign: 'center' as const }}>
                        {new Date(calYear, calMon).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button variant="ghost" size="xs" onClick={(e: any) => { e.stopPropagation(); const d = new Date(calYear, calMon + 1, 1); setSchedCalMonth(d.toISOString().slice(0, 7)); }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                      </Button>
                    </div>
                  </div>
                  {schedShowCalendar && (
                    <div style={cardStyle}>
                      {/* Day headers */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 8 }}>
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'].map(d => (
                          <div key={d} style={{ fontSize: 11, color: T.textMuted, textAlign: 'center' as const, padding: '4px 0', fontWeight: 500 }}>{d}</div>
                        ))}
                      </div>
                      {/* Day grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                          <div key={`empty-${i}`} style={{ minHeight: 48 }} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const dayNum = i + 1;
                          const dateStr = `${calYear}-${String(calMon + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                          const items = schedCalendar[dateStr] || [];
                          const isToday = dateStr === new Date().toISOString().slice(0, 10);
                          return (
                            <div key={dayNum} style={{
                              minHeight: 48, padding: 4, borderRadius: 8,
                              background: isToday ? T.primaryBg : T.bgSubtle,
                              border: isToday ? `1px solid ${T.primaryBgStrong}` : '1px solid transparent',
                            }}>
                              <div style={{ fontSize: 11, color: isToday ? T.primary : T.textSecondary, fontWeight: isToday ? 600 : 400, marginBottom: 2 }}>{dayNum}</div>
                              {items.length > 0 && (
                                <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' as const }}>
                                  {items.slice(0, 3).map((_: any, j: number) => (
                                    <div key={j} style={{ width: 6, height: 6, borderRadius: '50%', background: T.primary }} />
                                  ))}
                                  {items.length > 3 && <span style={{ fontSize: 9, color: T.textMuted }}>+{items.length - 3}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── d) History ── */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, cursor: 'pointer' }}
                    onClick={() => setSchedShowHistory(!schedShowHistory)}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' as const, letterSpacing: 1 }}>
                      Historico
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" style={{ marginLeft: 8, transform: schedShowHistory ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['', 'published', 'failed'].map(s => (
                        <Button variant="ghost" size="xs" onClick={(e: any) => { e.stopPropagation(); setSchedHistoryFilter(s); setSchedHistoryPage(1); }}>
                          {s === '' ? 'Todos' : s === 'published' ? 'Publicados' : 'Falhas'}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {schedShowHistory && (
                    <div style={cardStyle}>
                      {schedHistory.length === 0 && <div style={{ color: T.textMuted, fontSize: 13, textAlign: 'center' as const, padding: 20 }}>Nenhum item no historico.</div>}
                      <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
                        {schedHistory.length > 0 && (
                          <thead>
                            <tr>
                              {['Titulo', 'Agendado', 'Publicado', 'Status', 'Regra'].map(h => (
                                <th key={h} style={{ ...S.th, fontSize: 11, padding: '8px 10px' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {schedHistory.map((hi: any) => (
                            <tr key={hi.id}>
                              <td style={{ ...S.td, fontSize: 12, padding: '8px 10px' }}>{hi.entry_title || hi.entry_id}</td>
                              <td style={{ ...S.td, fontSize: 11, padding: '8px 10px', color: T.textSecondary }}>{hi.scheduled_at ? new Date(hi.scheduled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                              <td style={{ ...S.td, fontSize: 11, padding: '8px 10px', color: T.textSecondary }}>{hi.published_at ? new Date(hi.published_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                              <td style={{ ...S.td, padding: '8px 10px' }}><span style={statusBadge(hi.status)}>{hi.status}</span></td>
                              <td style={{ ...S.td, fontSize: 11, padding: '8px 10px', color: T.textMuted }}>{hi.rule_name || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {schedHistoryTotal > 20 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                          <Button variant="ghost" size="xs" onClick={() => setSchedHistoryPage(p => p - 1)} disabled={schedHistoryPage <= 1}>Anterior</Button>
                          <span style={{ fontSize: 11, color: T.textMuted, alignSelf: 'center' }}>{schedHistoryPage} / {Math.ceil(schedHistoryTotal / 20)}</span>
                          <Button variant="ghost" size="xs" onClick={() => setSchedHistoryPage(p => p + 1)} disabled={schedHistoryPage >= Math.ceil(schedHistoryTotal / 20)}>Proximo</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ═══ SCHEDULE RULE MODAL ═══ */}
      {schedShowRuleModal && (() => {
        const [formData, setFormData] = [schedEditRule || {
          name: '', content_type_id: types[0]?.id || 'blog', frequency_type: 'daily', frequency_value: 1,
          preferred_hours: [9, 14, 19], preferred_days: [1, 2, 3, 4, 5], timezone: 'America/Sao_Paulo',
          category_ids: [], max_per_day: 3, auto_translate: 0, auto_cover: 0, notify_telegram: 0,
        }, (v: any) => setSchedEditRule(typeof v === 'function' ? v(schedEditRule) : v)];

        const updateField = (key: string, val: any) => setSchedEditRule((prev: any) => ({ ...(prev || formData), [key]: val }));
        const toggleDay = (d: number) => {
          const cur = formData.preferred_days ? (typeof formData.preferred_days === 'string' ? JSON.parse(formData.preferred_days) : formData.preferred_days) : [1,2,3,4,5];
          updateField('preferred_days', cur.includes(d) ? cur.filter((x: number) => x !== d) : [...cur, d].sort());
        };
        const toggleHour = (h: number) => {
          const cur = formData.preferred_hours ? (typeof formData.preferred_hours === 'string' ? JSON.parse(formData.preferred_hours) : formData.preferred_hours) : [9,14,19];
          updateField('preferred_hours', cur.includes(h) ? cur.filter((x: number) => x !== h) : [...cur, h].sort());
        };

        const parsedDays = formData.preferred_days ? (typeof formData.preferred_days === 'string' ? JSON.parse(formData.preferred_days) : formData.preferred_days) : [1,2,3,4,5];
        const parsedHours = formData.preferred_hours ? (typeof formData.preferred_hours === 'string' ? JSON.parse(formData.preferred_hours) : formData.preferred_hours) : [9,14,19];

        const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 13, background: T.bgCard, border: `1px solid ${T.borderHover}`, borderRadius: 10, color: T.text, outline: 'none' } as any;
        const labelStyle = { fontSize: 12, color: T.textSecondary, marginBottom: 6, display: 'block' } as any;

        return (
          <div style={S.modalOverlay} onClick={() => { setSchedShowRuleModal(false); setSchedEditRule(null); }}>
            <div style={{ ...S.modal, maxWidth: 520 }} onClick={(e: any) => e.stopPropagation()}>
              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: T.text }}>{schedEditRule?.id ? 'Editar Regra' : 'Nova Regra de Agendamento'}</h2>

              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
                {/* Name */}
                <div>
                  <label style={labelStyle}>Nome da Regra</label>
                  <input style={inputStyle} value={formData.name || ''} onInput={(e: any) => updateField('name', e.target.value)} placeholder="Ex: Posts diarios do blog" />
                </div>

                {/* Content Type */}
                <div>
                  <label style={labelStyle}>Tipo de Conteudo</label>
                  <select style={inputStyle} value={formData.content_type_id || ''} onChange={(e: any) => updateField('content_type_id', e.target.value)}>
                    {types.map(t => <option key={t.id} value={t.id}>{t.label_plural || t.label}</option>)}
                  </select>
                </div>

                {/* Frequency */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Frequencia</label>
                    <select style={inputStyle} value={formData.frequency_type || 'daily'} onChange={(e: any) => updateField('frequency_type', e.target.value)}>
                      <option value="hourly">Por hora</option>
                      <option value="daily">Diario</option>
                      <option value="weekly">Semanal</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Intervalo</label>
                    <input type="number" min="1" style={inputStyle} value={formData.frequency_value || 1} onInput={(e: any) => updateField('frequency_value', parseInt(e.target.value) || 1)} />
                  </div>
                </div>

                {/* Days */}
                <div>
                  <label style={labelStyle}>Dias da Semana</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1,2,3,4,5,6,7].map(d => (
                      <Button variant="ghost" size="sm" onClick={() => toggleDay(d)}>
                        {dayLabels[d]}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Hours */}
                <div>
                  <label style={labelStyle}>Horarios Preferidos</label>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                    {[6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21].map(h => (
                      <Button variant="ghost" size="xs" onClick={() => toggleHour(h)}>
                        {`${String(h).padStart(2, '0')}:00`}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Max per day */}
                <div>
                  <label style={labelStyle}>Maximo por dia</label>
                  <input type="number" min="1" max="20" style={inputStyle} value={formData.max_per_day ?? 3} onInput={(e: any) => updateField('max_per_day', parseInt(e.target.value) || 3)} />
                </div>

                {/* Toggles */}
                <div style={{ display: 'flex', gap: 20 }}>
                  {[
                    { key: 'auto_translate', label: 'Auto-traducao' },
                    { key: 'auto_cover', label: 'Auto-capa' },
                    { key: 'notify_telegram', label: 'Notificar Telegram' },
                  ].map(({ key, label }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textSecondary, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!formData[key]} onChange={(e: any) => updateField(key, e.target.checked ? 1 : 0)}
                        style={{ width: 16, height: 16, accentColor: T.primary }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
                <Button variant="ghost" onClick={() => { setSchedShowRuleModal(false); setSchedEditRule(null); }}>Cancelar</Button>
                <Button variant="ghost" onClick={() => {
                  const data = {
                    name: formData.name || '', content_type_id: formData.content_type_id || types[0]?.id || 'blog',
                    frequency_type: formData.frequency_type || 'daily', frequency_value: formData.frequency_value || 1,
                    preferred_hours: parsedHours, preferred_days: parsedDays,
                    timezone: formData.timezone || 'America/Sao_Paulo', category_ids: formData.category_ids || [],
                    max_per_day: formData.max_per_day ?? 3, auto_translate: formData.auto_translate ?? 0,
                    auto_cover: formData.auto_cover ?? 0, notify_telegram: formData.notify_telegram ?? 0,
                  };
                  saveRule(data);
                }}>Salvar</Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ MENU MODAL ═══ */}
      {showMenuModal && (
        <div style={S.modalOverlay} onClick={() => setShowMenuModal(false)}>
          <div style={{ ...S.modal, maxWidth: 600 }} onClick={(e: any) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
              {editMenu ? 'Editar Menu' : 'Novo Menu'}
            </h2>

            <div style={S.row}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Slug</label>
                <input style={S.input} value={mfSlug} onInput={(e: any) => setMfSlug(e.target.value)} placeholder="main-nav" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Location</label>
                <input style={S.input} value={mfLocation} onInput={(e: any) => setMfLocation(e.target.value)} placeholder="header, footer, sidebar" />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Items JSON</label>
              <Textarea
                style={{ ...S.textarea, minHeight: 300 }}
                value={mfItemsJson}
                onInput={(e: any) => setMfItemsJson(e.target.value)}
                placeholder='[{"label": "Home", "url": "/"}]'
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShowMenuModal(false)}>Cancelar</Button>
              <Button variant="ghost" onClick={saveMenu} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* LLM Test Tab */}
      {subTab === 'llm-test' && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: T.text }}>Teste LLM</h2>
          <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Testar conexao e respostas do provedor LLM em tempo real.</p>

          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Conexao</h3>
            <div style={S.row}>
              <div style={{ flex: 2 }}>
                <label style={S.label}>URL da API</label>
                <input style={S.input} value={llmTestUrl} onInput={(e: any) => setLlmTestUrl(e.target.value)} placeholder="/api/v1/llm/v1" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Token (Bearer)</label>
                <input type="password" style={S.input} value={llmTestToken} onInput={(e: any) => setLlmTestToken(e.target.value)} placeholder="ert_live_..." />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button variant="ghost" onClick={async () => {
                setLlmTestFetchingModels(true);
                setLlmTestModels([]);
                try {
                  const url = llmTestUrl.replace(/\/$/, '') + '/models';
                  const headers: Record<string, string> = {};
                  if (llmTestToken) headers['Authorization'] = `Bearer ${llmTestToken}`;
                  const r = await fetch(url, { headers });
                  const json = await r.json() as any;
                  const data = json.data || json.models || json;
                  if (Array.isArray(data)) {
                    const models = data.map((m: any) => typeof m === 'string' ? m : m.id || m.model || '').filter(Boolean);
                    setLlmTestModels(models);
                    if (models.length && !llmTestModel) setLlmTestModel(models[0]);
                  }
                } catch (err: any) {
                  setLlmTestResult({ error: 'Falha ao buscar modelos: ' + (err.message || 'erro desconhecido') });
                }
                setLlmTestFetchingModels(false);
              }} disabled={llmTestFetchingModels}>
                {llmTestFetchingModels ? 'Buscando...' : 'Buscar Modelos'}
              </Button>
              {llmTestModels.length > 0 && <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 8 }}>{llmTestModels.length} modelos encontrados</span>}
            </div>
          </div>

          {llmTestModels.length > 0 && (
            <div style={{ ...S.card, marginTop: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Teste</h3>
              <div style={S.row}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Modelo</label>
                  <select style={S.select} value={llmTestModel} onChange={(e: any) => setLlmTestModel(e.target.value)}>
                    {llmTestModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={S.label}>Mensagem</label>
                <Textarea style={{ ...S.textarea, minHeight: 60 }} value={llmTestMsg} onInput={(e: any) => setLlmTestMsg(e.target.value)} />
              </div>
              <div style={{ marginTop: 12 }}>
                <Button variant="ghost" onClick={async () => {
                  setLlmTestLoading(true);
                  setLlmTestResult(null);
                  const startTime = Date.now();
                  try {
                    const url = llmTestUrl.replace(/\/$/, '') + '/chat/completions';
                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                    if (llmTestToken) headers['Authorization'] = `Bearer ${llmTestToken}`;
                    const r = await fetch(url, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({
                        model: llmTestModel,
                        messages: [{ role: 'user', content: llmTestMsg }],
                        max_tokens: 256,
                      }),
                    });
                    const latency = Date.now() - startTime;
                    const json = await r.json() as any;
                    const choice = json.choices?.[0];
                    const usage = json.usage || {};
                    setLlmTestResult({
                      ok: !!choice,
                      response: choice?.message?.content || JSON.stringify(json, null, 2),
                      latency,
                      model: json.model || llmTestModel,
                      provider: json.provider || json._provider || '-',
                      promptTokens: usage.prompt_tokens || 0,
                      completionTokens: usage.completion_tokens || 0,
                      totalTokens: usage.total_tokens || 0,
                      cost: json._cost_usd || json.cost || usage.cost || null,
                      raw: json,
                    });
                  } catch (err: any) {
                    setLlmTestResult({ error: err.message || 'Erro na requisicao' });
                  }
                  setLlmTestLoading(false);
                }} disabled={llmTestLoading || !llmTestModel}>
                  {llmTestLoading ? 'Testando...' : 'Testar'}
                </Button>
              </div>
            </div>
          )}

          {llmTestResult && (
            <div style={{ ...S.card, marginTop: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.text }}>Resultado</h3>
              {llmTestResult.error ? (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: T.dangerBg, color: T.danger, fontSize: 13 }}>{llmTestResult.error}</div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' as const }}>
                    {[
                      { label: 'Latencia', value: `${llmTestResult.latency}ms` },
                      { label: 'Modelo', value: llmTestResult.model },
                      { label: 'Provider', value: llmTestResult.provider },
                      { label: 'Tokens (prompt)', value: llmTestResult.promptTokens },
                      { label: 'Tokens (resposta)', value: llmTestResult.completionTokens },
                      { label: 'Tokens (total)', value: llmTestResult.totalTokens },
                      ...(llmTestResult.cost != null ? [{ label: 'Custo (USD)', value: `$${Number(llmTestResult.cost).toFixed(6)}` }] : []),
                    ].map(s => (
                      <div key={s.label} style={{ background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 8, padding: '8px 12px', minWidth: 100 }}>
                        <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 2 }}>{s.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 10, padding: 16 }}>
                    <label style={{ ...S.label, marginBottom: 6 }}>Resposta</label>
                    <pre style={{ color: T.text, fontSize: 13, whiteSpace: 'pre-wrap' as const, margin: 0, fontFamily: 'monospace', lineHeight: 1.5 }}>{llmTestResult.response}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
